<template>
  <div class="embedded-meeting-sidebar" :class="{'is-connected': isConnected, 'is-collapsed': isCollapsed}">
    <!-- Sidebar Header (Collapsible Toggle) -->
    <div class="sidebar-header">
      <div class="header-title" v-if="!isCollapsed">
        <span class="header-text">Meeting</span>
        <i class="el-icon-user-solid header-icon"></i>
      </div>
      <div class="header-title" v-else>
        <i class="el-icon-user-solid header-icon" style="margin-left: 8px"></i>
      </div>
      <button class="header-toggle-btn" @click="isCollapsed = !isCollapsed" :title="isCollapsed ? 'Expand' : 'Collapse'">
        <i :class="isCollapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'"></i>
      </button>
    </div>
    <!-- Connection Status / Loading -->
    <div v-if="isConnecting" class="sidebar-status">
      <div class="sidebar-spinner"></div>
      <div class="sidebar-status-text">Joining meeting...</div>
    </div>

    <div v-if="connectionError" class="sidebar-status sidebar-error">
      <i class="el-icon-warning-outline" style="font-size: 32px; color: #ef4444"></i>
      <div class="sidebar-status-text" style="color: #ef4444">{{ connectionError }}</div>
      <button class="sidebar-retry-btn" @click="connectToMeeting">Retry</button>
    </div>

    <div v-if="!isConnected && !isConnecting && !connectionError" class="sidebar-status">
      <i class="el-icon-video-camera" style="font-size: 32px; color: #94a3b8"></i>
      <div class="sidebar-status-text" style="color: #64748b">Meeting disconnected</div>
      <button class="sidebar-retry-btn" @click="connectToMeeting">Rejoin</button>
    </div>

    <!-- Participant Video Tiles -->
    <div v-if="isConnected" class="sidebar-tiles-container">
      <div class="sidebar-tiles" ref="tilesContainer">
        <!-- Private Channel Group Containers (One box per active channel) -->
        <div
          v-for="(group, gIdx) in activeChannelGroups"
          :key="'ch_group_' + (group.id || gIdx)"
          class="private-channel-group-container"
          :class="{
            'is-my-channel-container': group.isMyChannel && group.isTeacherChannel,
            'is-other-channel-container': !group.isMyChannel,
          }">
          <div v-for="p in group.participants" :key="'ch_' + (p.sid || p.identity)" class="sidebar-tile-wrapper in-channel-tile-wrapper">
            <div
              class="sidebar-tile"
              :class="{
                'is-teacher-tile': isParticipantTeacher(p),
                'is-local-tile': p.isLocal,
                'is-clickable-for-teacher':
                  isTeacher &&
                  !p.isLocal &&
                  !isParticipantTeacher(p) &&
                  !isStudentInAnotherTeacherChannel(p) &&
                  (!p.isPrivateSessionPartner || (channelStudents && channelStudents.length > 1)),
                'is-speaking': p.sid === activeSpeakerSid,
                'is-expanded': p.isMicOn && !isCollapsed,
                'is-blinking': p.isHandRaised && isTeacher && !isStudentInAnotherTeacherChannel(p),
                'has-no-badge': !isParticipantTeacher(p) && !p.isLocal,
              }"
              @click="handleTileClick(p)">
              <!-- Remove Button for Teacher on Student Card -->
              <button
                v-if="isTeacher && p.isPrivateSessionPartner && !p.isLocal && !isParticipantTeacher(p) && !isLiveDemoSession"
                class="student-remove-btn"
                @click.stop="openLeaveModal(p)"
                title="Remove">
                <svg
                  class="student-remove-icon"
                  viewBox="0 0 20 20"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <circle cx="7" cy="5.5" r="2.8" />
                  <path d="M2 16h10c0-2.4-2-4.2-5-4.2s-5 1.8-5 4.2z" />
                  <line x1="13.5" y1="11.5" x2="17.5" y2="15.5" />
                  <line x1="17.5" y1="11.5" x2="13.5" y2="15.5" />
                </svg>
                <span class="student-remove-text">Remove</span>
              </button>

              <!-- Collapsible Card View (No Video) -->
              <div v-if="isCollapsed" class="sidebar-collapsed-card">
                <span class="collapsed-name" :title="p.name">{{ p.name }}</span>
                <div v-if="isParticipantTeacher(p)" class="collapsed-badge teacher-badge">
                  <span class="badge-emoji">👩‍🏫</span>
                  <span class="badge-text">Teacher</span>
                </div>
                <div v-else-if="p.isLocal" class="collapsed-badge local-badge">You</div>
                <div class="collapsed-icons">
                  <svg
                    v-if="p.isMicOn && (p.isLocal || p.isPrivateSessionPartner || !p.isChannelMuted)"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="#20d999"
                    title="Microphone On">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="#ef4444" title="Microphone Muted">
                    <path
                      d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-1 .9-2.16.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                  </svg>
                  <svg v-if="p.isVideoOn" viewBox="0 0 24 24" width="18" height="18" fill="#131b2e" title="Camera On">
                    <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="#ef4444" title="Camera Off">
                    <path
                      d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2zM5 16V8h1.73l8 8H5z" />
                  </svg>
                </div>
              </div>

              <!-- Expanded State (Video / Avatar / Full Label) -->
              <template v-else>
                <!-- Help Request Notification Overlay on Student Card (Teacher View Only) -->
                <div v-if="p.isHandRaised && isTeacher && !isStudentInAnotherTeacherChannel(p)" class="raise-hand-bubble" @click.stop="handleTileClick(p)">
                  <span class="raise-hand-emoji">✋</span>
                  <span class="raise-hand-bubble-text">Raised Hand</span>
                  <button class="raise-hand-accept-btn" @click.stop="handleTileClick(p)">
                    <i class="fa fa-share"></i>
                  </button>
                </div>
                <video
                  v-show="!isCollapsed && p.isVideoOn && p.videoTrack"
                  autoplay
                  playsinline
                  :muted="p.isLocal"
                  class="sidebar-video"
                  :class="{'team-channel-blurred': p.isBlurred}"
                  :ref="'video_' + p.sid"></video>
                <div v-if="!isCollapsed && (!p.isVideoOn || !p.videoTrack)" class="sidebar-avatar" :class="{'team-channel-blurred': p.isBlurred}">
                  <div class="sidebar-avatar-circle" :class="isParticipantTeacher(p) ? 'is-teacher-circle' : 'is-student-circle'">
                    {{ getInitials(p.name) }}
                  </div>
                </div>

                <div class="sidebar-tile-label">
                  <div class="sidebar-tile-name-group">
                    <span class="sidebar-tile-name" :title="p.name">{{ p.name }}</span>
                    <span v-if="isParticipantTeacher(p) && !isTeacher" class="sidebar-teacher-badge">Teacher</span>
                    <span v-else-if="p.isLocal && !isParticipantTeacher(p)" class="sidebar-local-badge">You</span>
                  </div>
                  <div class="sidebar-tile-icons">
                    <!-- Audio Mic Icon with Dynamic Green Level Fill -->
                    <svg
                      v-if="p.isMicOn && (p.isLocal || p.isPrivateSessionPartner || !p.isChannelMuted)"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      class="sidebar-icon mic-svg"
                      title="Microphone On">
                      <defs>
                        <clipPath :id="'mic-clip-' + p.sid">
                          <rect x="0" :y="14 - (p.audioLevel || 0) * 12" width="24" :height="(p.audioLevel || 0) * 12" />
                        </clipPath>
                      </defs>
                      <!-- Background outline of capsule -->
                      <path fill="rgba(255,255,255,0.4)" d="M12,14c1.66,0,3-1.34,3-3V5c0-1.66-1.34-3-3-3S9,3.34,9,5v6C9,12.66,10.34,14,12,14z" />
                      <!-- Dynamic green fill inside capsule -->
                      <path
                        fill="#22c55e"
                        d="M12,14c1.66,0,3-1.34,3-3V5c0-1.66-1.34-3-3-3S9,3.34,9,5v6C9,12.66,10.34,14,12,14z"
                        :clip-path="'url(#mic-clip-' + p.sid + ')'" />
                      <!-- Mic Stand -->
                      <path fill="#22c55e" d="M17,11c0,2.76-2.24,5-5,5s-5-2.24-5-5H5c0,3.53,2.61,6.43,6,6.92V21h2v-3.08c3.39-0.49,6-3.39,6-6.92H17z" />
                    </svg>
                    <i v-else class="fa fa-microphone-slash is-muted" title="Microphone Muted" style="font-size: 13px; color: #ef4444"></i>

                    <!-- Camera Icon -->
                    <i
                      :class="[p.isVideoOn ? 'fa fa-video-camera' : 'fa fa-video-camera is-off-cam', {'is-muted': !p.isVideoOn}]"
                      :title="p.isVideoOn ? 'Camera On' : 'Camera Off'"
                      style="font-size: 13px"></i>

                    <!-- Raise Hand Icon -->
                    <i
                      v-if="p.isLocal && !isTeacher"
                      class="fa fa-hand-paper-o raise-hand-icon"
                      :class="{'is-hand-raised': p.isHandRaised, 'is-disabled': isStudentInTeacherChannel}"
                      @click.stop="!isStudentInTeacherChannel && toggleRaiseHand(p)"
                      :title="isStudentInTeacherChannel ? 'Already connected with teacher in a channel' : p.isHandRaised ? 'Lower Hand' : 'Raise Hand'"
                      style="font-size: 13px"
                      :style="{
                        color: isStudentInTeacherChannel ? 'rgba(255, 255, 255, 0.3)' : p.isHandRaised ? '#fbbf24' : 'rgba(255, 255, 255, 0.7)',
                        cursor: isStudentInTeacherChannel ? 'not-allowed' : 'pointer',
                        marginLeft: '4px',
                      }"></i>
                  </div>
                </div>
              </template>
            </div>

            <button
              v-if="!isTeacher && p.isPrivateSessionPartner && !p.isLocal && !isParticipantTeacher(p) && !isLiveDemoSession"
              @click.stop="openLeaveModal(p)"
              style="
                margin-top: 6px;
                background: #cc0000;
                color: white;
                border: 1px dashed #0ea5e9;
                width: 85%;
                padding: 6px 12px;
                border-radius: 4px;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
              ">
              Leave <i class="fa fa-sign-out"></i>
            </button>
          </div>
        </div>

        <!-- Other Participants Outside Private Channel -->
        <div
          v-for="p in otherParticipants"
          :key="'oth_' + (p.sid || p.identity)"
          class="sidebar-tile-wrapper"
          :class="{'is-top-teacher-wrapper': isParticipantTeacher(p)}">
          <div
            class="sidebar-tile"
            :class="{
              'is-teacher-tile': isParticipantTeacher(p),
              'is-local-tile': p.isLocal,
              'is-clickable-for-teacher':
                isTeacher &&
                !p.isLocal &&
                !isParticipantTeacher(p) &&
                !isStudentInAnotherTeacherChannel(p) &&
                (!p.isPrivateSessionPartner || (channelStudents && channelStudents.length > 1)),
              'is-speaking': p.sid === activeSpeakerSid,
              'is-expanded': p.isMicOn && !isCollapsed,
              'is-blinking': p.isHandRaised && isTeacher && !isStudentInAnotherTeacherChannel(p),
              'has-no-badge': !isParticipantTeacher(p) && !p.isLocal,
            }"
            @click="handleTileClick(p)">
            <!-- Remove Button for Teacher on Student Card -->
            <button
              v-if="isTeacher && p.isPrivateSessionPartner && !p.isLocal && !isParticipantTeacher(p) && !isLiveDemoSession"
              class="student-remove-btn"
              @click.stop="openLeaveModal(p)"
              title="Remove">
              <svg
                class="student-remove-icon"
                viewBox="0 0 20 20"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round">
                <circle cx="7" cy="5.5" r="2.8" />
                <path d="M2 16h10c0-2.4-2-4.2-5-4.2s-5 1.8-5 4.2z" />
                <line x1="13.5" y1="11.5" x2="17.5" y2="15.5" />
                <line x1="17.5" y1="11.5" x2="13.5" y2="15.5" />
              </svg>
              <span class="student-remove-text">Remove</span>
            </button>

            <!-- Collapsible Card View (No Video) -->
            <div v-if="isCollapsed" class="sidebar-collapsed-card">
              <span class="collapsed-name" :title="p.name">{{ p.name }}</span>
              <div v-if="isParticipantTeacher(p)" class="collapsed-badge teacher-badge">
                <span class="badge-emoji">👩‍🏫</span>
                <span class="badge-text">Teacher</span>
              </div>
              <div v-else-if="p.isLocal" class="collapsed-badge local-badge">You</div>
              <div class="collapsed-icons">
                <svg v-if="p.isMicOn && (p.isLocal || !p.isChannelMuted)" viewBox="0 0 24 24" width="18" height="18" fill="#20d999" title="Microphone On">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="#ef4444" title="Microphone Muted">
                  <path
                    d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-1 .9-2.16.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                </svg>
                <svg v-if="p.isVideoOn" viewBox="0 0 24 24" width="18" height="18" fill="#131b2e" title="Camera On">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="#ef4444" title="Camera Off">
                  <path
                    d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2zM5 16V8h1.73l8 8H5z" />
                </svg>
              </div>
            </div>

            <!-- Expanded State (Video / Avatar / Full Label) -->
            <template v-else>
              <!-- Help Request Notification Overlay on Student Card (Teacher View Only) -->
              <div v-if="p.isHandRaised && isTeacher && !isStudentInAnotherTeacherChannel(p)" class="raise-hand-bubble" @click.stop="handleTileClick(p)">
                <span class="raise-hand-emoji">✋</span>
                <span class="raise-hand-bubble-text">Raised Hand</span>
                <button class="raise-hand-accept-btn" @click.stop="handleTileClick(p)">
                  <i class="fa fa-share"></i>
                </button>
              </div>
              <video
                v-show="!isCollapsed && p.isVideoOn && p.videoTrack"
                autoplay
                playsinline
                :muted="p.isLocal"
                class="sidebar-video"
                :class="{'team-channel-blurred': p.isBlurred}"
                :ref="'video_' + p.sid"></video>
              <div v-if="!isCollapsed && (!p.isVideoOn || !p.videoTrack)" class="sidebar-avatar" :class="{'team-channel-blurred': p.isBlurred}">
                <div class="sidebar-avatar-circle" :class="isParticipantTeacher(p) ? 'is-teacher-circle' : 'is-student-circle'">
                  {{ getInitials(p.name) }}
                </div>
              </div>

              <div class="sidebar-tile-label">
                <div class="sidebar-tile-name-group">
                  <span class="sidebar-tile-name" :title="p.name">{{ p.name }}</span>
                  <span v-if="isParticipantTeacher(p) && !isTeacher" class="sidebar-teacher-badge">Teacher</span>
                  <span v-else-if="p.isLocal && !isParticipantTeacher(p)" class="sidebar-local-badge">You</span>
                </div>
                <div class="sidebar-tile-icons">
                  <!-- Audio Mic Icon with Dynamic Green Level Fill -->
                  <svg
                    v-if="p.isMicOn && (p.isLocal || !p.isChannelMuted)"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    class="sidebar-icon mic-svg"
                    title="Microphone On">
                    <defs>
                      <clipPath :id="'mic-clip-' + p.sid">
                        <rect x="0" :y="14 - (p.audioLevel || 0) * 12" width="24" :height="(p.audioLevel || 0) * 12" />
                      </clipPath>
                    </defs>
                    <!-- Background outline of capsule -->
                    <path fill="rgba(255,255,255,0.4)" d="M12,14c1.66,0,3-1.34,3-3V5c0-1.66-1.34-3-3-3S9,3.34,9,5v6C9,12.66,10.34,14,12,14z" />
                    <!-- Dynamic green fill inside capsule -->
                    <path
                      fill="#22c55e"
                      d="M12,14c1.66,0,3-1.34,3-3V5c0-1.66-1.34-3-3-3S9,3.34,9,5v6C9,12.66,10.34,14,12,14z"
                      :clip-path="'url(#mic-clip-' + p.sid + ')'" />
                    <!-- Mic Stand -->
                    <path fill="#22c55e" d="M17,11c0,2.76-2.24,5-5,5s-5-2.24-5-5H5c0,3.53,2.61,6.43,6,6.92V21h2v-3.08c3.39-0.49,6-3.39,6-6.92H17z" />
                  </svg>
                  <i v-else class="fa fa-microphone-slash is-muted" title="Microphone Muted" style="font-size: 13px; color: #ef4444"></i>

                  <!-- Camera Icon -->
                  <i
                    :class="[p.isVideoOn ? 'fa fa-video-camera' : 'fa fa-video-camera is-off-cam', {'is-muted': !p.isVideoOn}]"
                    :title="p.isVideoOn ? 'Camera On' : 'Camera Off'"
                    style="font-size: 13px"></i>

                  <!-- Raise Hand Icon -->
                  <i
                    v-if="p.isLocal && !isTeacher"
                    class="fa fa-hand-paper-o raise-hand-icon"
                    :class="{'is-hand-raised': p.isHandRaised, 'is-disabled': isStudentInTeacherChannel}"
                    @click.stop="!isStudentInTeacherChannel && toggleRaiseHand(p)"
                    :title="isStudentInTeacherChannel ? 'Already connected with teacher in a channel' : p.isHandRaised ? 'Lower Hand' : 'Raise Hand'"
                    style="font-size: 13px"
                    :style="{
                      color: isStudentInTeacherChannel ? 'rgba(255, 255, 255, 0.3)' : p.isHandRaised ? '#fbbf24' : 'rgba(255, 255, 255, 0.7)',
                      cursor: isStudentInTeacherChannel ? 'not-allowed' : 'pointer',
                      marginLeft: '4px',
                    }"></i>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Controls -->
    <div v-if="isConnected" class="sidebar-bottom-controls">
      <!-- Top Row: Student Actions (Raise Hand & Peer Help) -->
      <div v-if="!isTeacher" class="footer-actions-row">
        <button
          class="footer-action-btn raise-hand-btn"
          :class="[localParticipant && localParticipant.isHandRaised ? 'lower-hand-btn' : 'raise-hand-btn', {'is-disabled': isStudentInTeacherChannel}]"
          :disabled="isStudentInTeacherChannel"
          :title="
            isStudentInTeacherChannel
              ? 'Already connected with teacher in a channel'
              : localParticipant && localParticipant.isHandRaised
              ? 'Lower Hand'
              : 'Raise Hand'
          "
          @click="!isStudentInTeacherChannel && toggleRaiseHand(localParticipant)">
          <span class="footer-btn-emoji">✋</span>
          <span class="footer-btn-text">{{ localParticipant && localParticipant.isHandRaised ? 'Lower Hand' : 'Raise Hand' }}</span>
        </button>

        <!-- <button
          class="footer-action-btn peer-help-btn"
          :class="{'is-disabled': isPeerHelpDisabled}"
          :disabled="isPeerHelpDisabled"
          @click="!isPeerHelpDisabled && handlePeerHelpClick()"
          :title="peerHelpDisabledTooltip">
          <span class="footer-btn-emoji">🤝</span>
          <span class="footer-btn-text">Peer Help</span>
        </button> -->
      </div>

      <!-- Bottom Row: Media Controls (Aligned Right) -->
      <div class="footer-media-row">
        <button class="sidebar-ctrl-btn" :class="{'is-muted-btn': !isMicOn}" @click="toggleMic" :title="isMicOn ? 'Mute Microphone' : 'Unmute Microphone'">
          <i :class="isMicOn ? 'fa fa-microphone' : 'fa fa-microphone-slash'"></i>
        </button>

        <button class="sidebar-ctrl-btn" :class="{'is-muted-btn': !isVideoOn}" @click="toggleVideo" :title="isVideoOn ? 'Turn Off Camera' : 'Turn On Camera'">
          <i :class="isVideoOn ? 'fa fa-video-camera' : 'fa fa-video-camera'"></i>
        </button>
      </div>
    </div>

    <!-- Help Student Confirmation Modal -->
    <div v-if="helpModalStudent" class="help-student-modal-overlay" @click.self="closeHelpModal">
      <div class="help-student-modal-card">
        <div class="help-modal-header">
          <h3 class="help-modal-title">Help Student</h3>
          <button class="help-modal-close-btn" @click="closeHelpModal" title="Close">
            <i class="el-icon-close" style="font-size: 18px"></i>
          </button>
        </div>

        <div class="help-modal-body">
          <div v-if="isHelpModalFromRaiseHand" class="help-modal-student-headline">
            <span class="help-modal-student-name">{{ helpModalStudent.name }}</span>
            <span>has ✋ Raised hand</span>
          </div>

          <p class="help-modal-desc">You can help the student individually by joining their channel</p>

          <div class="help-modal-info-box">
            <i class="el-icon-info help-modal-info-icon"></i>
            <span class="help-modal-info-text"> Your screen will be changed to his screen and you can directly communicate with the student </span>
          </div>
        </div>

        <div class="help-modal-footer">
          <button class="help-modal-action-btn" @click="confirmHelpStudent">Help Student</button>
        </div>
      </div>
    </div>

    <!-- Leave Channel Confirmation Modal -->
    <div v-if="leaveModalStudent" class="help-student-modal-overlay" @click.self="closeLeaveModal">
      <div class="help-student-modal-card">
        <div class="help-modal-header">
          <h3 class="help-modal-title">{{ isTeacher ? 'Remove student' : 'Leave channel' }}</h3>
          <button class="help-modal-close-btn" @click="closeLeaveModal" title="Close">
            <i class="el-icon-close" style="font-size: 18px"></i>
          </button>
        </div>

        <div class="help-modal-body">
          <p class="help-modal-desc leave-modal-desc">
            <template v-if="isTeacher">
              You can remove <strong>{{ leaveModalStudent.name }}</strong> from this channel if their doubts are resolved
            </template>
            <template v-else>
              You can leave this channel with <strong>{{ leaveModalStudent.name }}</strong> if their doubts are resolved
            </template>
          </p>

          <div class="help-modal-info-box">
            <i class="el-icon-info help-modal-info-icon"></i>
            <span class="help-modal-info-text">
              {{
                isTeacher
                  ? 'After removing this student, you will return to the default view if no other students are in the channel.'
                  : 'After leaving this channel you will be back to your default view .'
              }}
            </span>
          </div>
        </div>

        <div class="help-modal-footer">
          <button class="help-modal-action-btn" @click="confirmLeaveChannel">
            {{ isTeacher ? 'Remove' : 'Leave' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Teacher Busy Confirmation / Info Modal for Students -->
    <div v-if="teacherBusyModalVisible" class="help-student-modal-overlay" @click.self="closeTeacherBusyModal">
      <div class="help-student-modal-card">
        <div class="help-modal-header">
          <h3 class="help-modal-title">Teacher is Currently Helping</h3>
          <button class="help-modal-close-btn" @click="closeTeacherBusyModal" title="Close">
            <i class="el-icon-close" style="font-size: 18px"></i>
          </button>
        </div>

        <div class="help-modal-body">
          <p class="help-modal-desc">
            The teacher is currently helping <strong v-if="busyStudentName">{{ busyStudentName }}</strong
            ><span v-else>another student</span> in a private channel.
          </p>

          <div class="help-modal-info-box">
            <i class="el-icon-time help-modal-info-icon" style="color: #f59e0b"></i>
            <span class="help-modal-info-text"> Please be patient! You can raise your hand once the teacher has finished assisting and is available. </span>
          </div>
        </div>

        <div class="help-modal-footer">
          <button class="help-modal-action-btn" @click="closeTeacherBusyModal">Got it</button>
        </div>
      </div>
    </div>

    <!-- Teacher Channel Switch / Group Invite Modal -->
    <div v-if="channelSwitchModalStudent" class="help-student-modal-overlay" @click.self="closeChannelSwitchModal">
      <div class="help-student-modal-card" style="max-width: 500px">
        <div class="help-modal-header">
          <h3 class="help-modal-title">Connect with {{ channelSwitchModalStudent.name }}</h3>
          <button class="help-modal-close-btn" @click="closeChannelSwitchModal" title="Close">
            <i class="el-icon-close" style="font-size: 18px"></i>
          </button>
        </div>

        <div class="help-modal-body">
          <div v-if="isChannelSwitchModalFromRaiseHand" class="help-modal-student-headline" style="margin-bottom: 12px">
            <span class="help-modal-student-name">{{ channelSwitchModalStudent.name }}</span>
            <span>has ✋ Raised hand</span>
          </div>

          <p class="help-modal-desc" style="margin-bottom: 16px; color: #475569; font-size: 13px">
            You are currently working with <strong>{{ currentActiveChannelStudentsNames }}</strong
            >. Please select how you would like to proceed:
          </p>

          <!-- Option 1: End Current Session & Switch to Student 2 -->
          <div
            class="channel-option-card"
            @click="handleOptionSwitchChannel"
            style="
              display: flex;
              gap: 12px;
              padding: 14px 16px;
              background: #f8fafc;
              border: 1.5px solid #e2e8f0;
              border-radius: 8px;
              cursor: pointer;
              margin-bottom: 12px;
              transition: all 0.2s ease;
            ">
            <div style="font-size: 22px; color: #0284c7; padding-top: 2px">
              <i class="el-icon-refresh"></i>
            </div>
            <div>
              <div style="font-weight: 600; font-size: 14px; color: #1e293b; margin-bottom: 4px">
                End Current Session &amp; Switch to {{ channelSwitchModalStudent.name }}
              </div>
              <div style="font-size: 12px; color: #64748b; line-height: 1.45">
                Conclude the session with {{ currentActiveChannelStudentsNames }} and start a new 1-on-1 private channel with
                {{ channelSwitchModalStudent.name }}.
              </div>
            </div>
          </div>

          <!-- Option 2: Add Student 2 to Current Channel -->
          <div
            class="channel-option-card"
            @click="handleOptionInviteToCurrentChannel"
            style="
              display: flex;
              gap: 12px;
              padding: 14px 16px;
              background: #f0fdf4;
              border: 1.5px solid #86efac;
              border-radius: 8px;
              cursor: pointer;
              margin-bottom: 12px;
              transition: all 0.2s ease;
            ">
            <div style="font-size: 22px; color: #16a34a; padding-top: 2px">
              <i class="el-icon-circle-plus-outline"></i>
            </div>
            <div>
              <div style="font-weight: 600; font-size: 14px; color: #15803d; margin-bottom: 4px">
                Invite {{ channelSwitchModalStudent.name }} to Current Session
              </div>
              <div style="font-size: 12px; color: #475569; line-height: 1.45">
                Add {{ channelSwitchModalStudent.name }} to this channel. You can switch between students' whiteboards using tabs, and students will view the
                selected board in real-time.
              </div>
            </div>
          </div>
        </div>

        <div class="help-modal-footer" style="justify-content: flex-end">
          <button
            class="channel-modal-cancel-btn"
            @click="closeChannelSwitchModal"
            style="
              background: transparent;
              border: 1px solid #cbd5e1;
              color: #475569;
              border-radius: 6px;
              padding: 6px 16px;
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
            ">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {Room, RoomEvent, VideoPresets, Track} from 'livekit-client'

const App = window.App

export default {
  name: 'EmbeddedMeetingSidebar',

  props: {
    sid: {type: String, required: true},
    userName: {type: String, required: true},
    uid: {type: String, default: ''},
    isTeacher: {type: Boolean, default: false},
    audioMuted: {type: Boolean, default: true},
    videoMuted: {type: Boolean, default: true},
    roomName: {type: String, default: ''},
  },

  data() {
    return {
      isConnecting: false,
      isConnected: false,
      isCollapsed: true,
      connectionError: '',
      isMicOn: !this.audioMuted,
      isVideoOn: !this.videoMuted,
      activeSpeakerSid: null,
      participants: [],
      room: null,
      audioLevelInterval: null,
      gameOnRecord: null,
      activeTeacherJoinedTeamId: null,
      handRaiseTimers: {},
      activePrivateSessions: {},
      lastStudentHelpInfo: null,
      cpaDrawUrl: '',
      isLiveDemoSession: false,
      helpModalStudent: null,
      isHelpModalFromRaiseHand: false,
      leaveModalStudent: null,
      teacherBusyModalVisible: false,
      channelSwitchModalStudent: null,
      isChannelSwitchModalFromRaiseHand: false,
      channelStudents: [],
      activeWhiteboardStudentIdentity: '',
      isPeerHelpLoading: false,
      isPeerHelpActive: false,
      hasHandRaiseTimedOut: false,
    }
  },

  computed: {
    isPeerHelpDisabled() {
      if (this.isPeerHelpLoading) return true
      // 1. When student raise hand and teacher acceptance is in progress for 60 seconds
      if (this.localParticipant && this.localParticipant.isHandRaised) return true
      // 2. When teacher is in a channel with student
      if (this.isStudentInTeacherChannel) return true
      // 3. When one student is already helping after clicking on peer help button
      if (this.isPeerHelpActive) return true
      // 4. Initially disabled until student raises hand and 60-second teacher response timer expires without teacher acceptance
      if (!this.hasHandRaiseTimedOut) return true
      return false
    },
    peerHelpDisabledTooltip() {
      if (this.isPeerHelpLoading) return 'Sending help request...'
      if (this.localParticipant && this.localParticipant.isHandRaised) {
        return 'Waiting for teacher response'
      }
      if (this.isStudentInTeacherChannel) {
        return 'Already connected with teacher in a channel'
      }
      if (this.isPeerHelpActive) {
        return 'A teammate is already helping'
      }
      if (!this.hasHandRaiseTimedOut) {
        return 'Raise hand to ask teacher for help first'
      }
      return 'Ask your team for help!'
    },
    channelStorageKey() {
      const sid = this.sid || (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.sid) || this.roomName || 'default'
      return `lk_channel_session_${sid}`
    },
    participantIdentity() {
      let userUid = window.currentUserId || this.$route?.query?.uid || this.uid
      if (!userUid) {
        userUid = sessionStorage.getItem('livekit_guest_uid')
        if (!userUid) {
          userUid = 'guest-' + Math.random().toString(36).substring(2, 11)
          sessionStorage.setItem('livekit_guest_uid', userUid)
        }
      }
      console.log('lksdlkjaksjd', userUid)
      return userUid
    },
    isStudentInTeacherChannel() {
      if (this.isTeacher) return false
      if (this.activePrivateSessions['ALL']) return true
      const myParticipant = this.room?.localParticipant
      const myUid = String(this.participantIdentity || this.uid || '').trim()
      const myIdent = String(myParticipant?.identity || '').trim()
      const myPartner = this.getParticipantPartnerIdentity(myParticipant || myIdent)
      if (myPartner && this.isIdentityTeacher(myPartner)) return true
      const myIds = this.getParticipantIds(myParticipant || myIdent)
      return Object.keys(this.activePrivateSessions).some((k) => {
        const val = String(this.activePrivateSessions[k] || '').trim()
        const isMe = myIds.some((id) => id === k || k.includes(id) || id === val || val.includes(id))
        const isTeacher = this.isIdentityTeacher(k) || this.isIdentityTeacher(val)
        return isMe && isTeacher
      })
    },
    isStudentInPeerChannel() {
      if (this.isTeacher) return false
      if (this.activePrivateSessions['ALL']) return false
      if (this.isPeerHelpActive) return true
      const myParticipant = this.room?.localParticipant
      const myUid = String(this.participantIdentity || this.uid || '').trim()
      const myIdent = String(myParticipant?.identity || '').trim()
      const myPartner = this.getParticipantPartnerIdentity(myParticipant || myIdent)
      if (myPartner && !this.isIdentityTeacher(myPartner)) return true
      const myIds = this.getParticipantIds(myParticipant || myIdent)
      return Object.keys(this.activePrivateSessions).some((k) => {
        const val = String(this.activePrivateSessions[k] || '').trim()
        if (this.isIdentityTeacher(k) || this.isIdentityTeacher(val)) return false
        return myIds.some((id) => id === k || k.includes(id) || id === val || val.includes(id))
      })
    },
    isTeacherBusyWithAnotherStudent() {
      if (this.isTeacher) return false
      if (this.isStudentInTeacherChannel) return false
      if (this.activePrivateSessions['ALL'] != null) return true
      if (this.activeTeacherJoinedTeamId != null) return true
      const teacherParticipants = this.participants.filter((p) => this.isParticipantTeacher(p))
      const activeStudentIds = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
      const busyTeacherIdentities = new Set()
      activeStudentIds.forEach((studentId) => {
        const partnerId = String(this.activePrivateSessions[studentId] || '').trim()
        if (this.isIdentityTeacher(partnerId)) {
          busyTeacherIdentities.add(partnerId)
        }
      })
      if (teacherParticipants.length > 0 && busyTeacherIdentities.size < teacherParticipants.length) {
        return false
      }
      return busyTeacherIdentities.size > 0
    },
    busyStudentName() {
      const activeStudentIds = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
      const teacherSessionStudentId = activeStudentIds.find((studentId) => {
        const partnerId = this.activePrivateSessions[studentId]
        return this.isIdentityTeacher(partnerId) || partnerId === 'ALL'
      })
      if (!teacherSessionStudentId) return ''
      const p = this.participants.find((item) => item.identity === teacherSessionStudentId || item.uid === teacherSessionStudentId)
      return p ? p.name : ''
    },
    isTeacherInAnyChannel() {
      if (!this.isTeacher) return false
      if (this.activeTeacherJoinedTeamId != null) return true
      if (this.activePrivateSessions['ALL'] != null) return true
      const myParticipant = this.room?.localParticipant
      const myIds = this.getParticipantIds(myParticipant)
      const activeStudentIds = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
      return activeStudentIds.some((sId) => {
        const partner = String(this.activePrivateSessions[sId] || '').trim()
        return myIds.some((id) => id === partner || partner.includes(id) || id === sId || sId.includes(id))
      })
    },
    currentActiveChannelStudents() {
      const activeStudentIdentities = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
      return this.participants.filter(
        (p) => !p.isLocal && (activeStudentIdentities.includes(p.identity) || (p.uid && activeStudentIdentities.includes(p.uid)) || p.isPrivateSessionPartner)
      )
    },
    currentActiveChannelStudentsNames() {
      const names = this.currentActiveChannelStudents.map((s) => s.name || s.identity)
      if (names.length === 0) return 'another student'
      if (names.length === 1) return names[0]
      if (names.length === 2) return `${names[0]} and ${names[1]}`
      return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
    },
    sortedParticipants() {
      let list = [...this.participants]
      const seen = new Map()
      list.forEach((p) => {
        // Hide backend recording connections
        const pName = p.name || ''
        const pId = p.identity || ''
        if (
          pName === 'Unknown' ||
          pName.includes('_demo') ||
          pName.includes('_Recorder') ||
          pName.includes('recorder-bot') ||
          pId.includes('_demo') ||
          pId.includes('_Recorder') ||
          pId.includes('recorder-bot')
        ) {
          return
        }

        // Deduplicate by normalized name or identity so demo recording or duplicate sessions don't create extra cards
        const normName = p.name ? p.name.trim().toLowerCase() : ''
        const key = normName || p.identity || ''
        if (!seen.has(key)) {
          seen.set(key, p)
        } else {
          const existing = seen.get(key)
          // Prefer local participant, or participant with active camera/mic
          if (p.isLocal || (!existing.isVideoOn && p.isVideoOn) || (!existing.isMicOn && p.isMicOn)) {
            seen.set(key, p)
          }
        }
      })
      list = Array.from(seen.values())

      return list.sort((a, b) => {
        // 1. Teacher always on top for both teacher and student
        const aTeacher = this.isParticipantTeacher(a)
        const bTeacher = this.isParticipantTeacher(b)
        if (aTeacher && !bTeacher) return -1
        if (!aTeacher && bTeacher) return 1

        // 2. Student connected with teacher in the same channel comes right after teacher
        const aInChannel = (a.isInPrivateChannel || a.isPrivateSessionPartner) && !aTeacher
        const bInChannel = (b.isInPrivateChannel || b.isPrivateSessionPartner) && !bTeacher
        if (aInChannel && !bInChannel) return -1
        if (!aInChannel && bInChannel) return 1

        // 3. Local participant ("Me") next
        if (a.isLocal && !b.isLocal) return -1
        if (!a.isLocal && b.isLocal) return 1

        // 4. Unmuted participants
        if (a.isMicOn && !b.isMicOn) return -1
        if (!a.isMicOn && b.isMicOn) return 1

        // 5. Alphabetical
        return (a.name || '').localeCompare(b.name || '')
      })
    },
    myChannelParticipants() {
      const myParticipant = this.room?.localParticipant
      const amITeacher = this.isTeacher || this.isParticipantTeacher(myParticipant)
      const amIInPrivateSession = amITeacher
        ? this.isTeacherInAnyChannel
        : this.isStudentInTeacherChannel || this.isStudentInPeerChannel || this.isPeerHelpActive

      if (!amIInPrivateSession) return []
      return this.sortedParticipants.filter((p) => p.isLocal || p.isPrivateSessionPartner)
    },
    otherChannelGroups() {
      const myParticipantIds = new Set(this.myChannelParticipants.map((p) => p.sid || p.identity))
      const remainingChannelParticipants = this.sortedParticipants.filter((p) => !myParticipantIds.has(p.sid || p.identity) && p.isInPrivateChannel)

      if (remainingChannelParticipants.length === 0) return []

      if (this.activePrivateSessions['ALL'] != null || this.activeTeacherJoinedTeamId != null) {
        return [remainingChannelParticipants]
      }

      const groupsMap = new Map()

      remainingChannelParticipants.forEach((p) => {
        const pIds = this.getParticipantIds(p)
        const isTeacher = this.isParticipantTeacher(p)
        let channelKey = ''

        if (isTeacher) {
          const matchedStudentId = Object.keys(this.activePrivateSessions).find((sId) => {
            if (sId === 'ALL') return false
            const partner = String(this.activePrivateSessions[sId] || '').trim()
            return pIds.some((id) => id === partner || partner.includes(id))
          })
          channelKey = matchedStudentId ? String(this.activePrivateSessions[matchedStudentId]).trim() : p.identity || p.uid || ''
        } else {
          const partner = this.getParticipantPartnerIdentity(p)
          if (partner) {
            if (this.isIdentityTeacher(partner)) {
              channelKey = partner
            } else {
              const myFirstId = p.identity || p.uid || ''
              channelKey = [myFirstId, partner].sort().join('___')
            }
          }
        }

        if (!channelKey) {
          channelKey = p.identity || p.uid || p.sid || 'unknown_channel'
        }

        if (!groupsMap.has(channelKey)) {
          groupsMap.set(channelKey, [])
        }
        groupsMap.get(channelKey).push(p)
      })

      return Array.from(groupsMap.values())
    },
    activeChannelGroups() {
      const groups = []
      if (this.myChannelParticipants.length > 0) {
        groups.push({
          id: 'my_channel',
          isMyChannel: true,
          isTeacherChannel: this.myChannelParticipants.some((p) => this.isParticipantTeacher(p)),
          participants: this.myChannelParticipants,
        })
      }
      this.otherChannelGroups.forEach((g, idx) => {
        if (g.length > 0) {
          groups.push({
            id: 'other_channel_' + idx,
            isMyChannel: false,
            isTeacherChannel: g.some((p) => this.isParticipantTeacher(p)),
            participants: g,
          })
        }
      })
      return groups
    },
    channelGroupParticipants() {
      return this.myChannelParticipants.length > 0 ? this.myChannelParticipants : this.activeChannelGroups[0]?.participants || []
    },
    otherParticipants() {
      const channelParticipantIds = new Set()
      this.activeChannelGroups.forEach((group) => {
        group.participants.forEach((p) => {
          channelParticipantIds.add(p.sid || p.identity)
          if (p.uid) channelParticipantIds.add(p.uid)
        })
      })
      return this.sortedParticipants.filter((p) => {
        const sid = p.sid || p.identity
        const uid = p.uid
        return !channelParticipantIds.has(sid) && (!uid || !channelParticipantIds.has(uid))
      })
    },
  },

  watch: {
    isCollapsed: {
      handler(val) {
        this.$emit('collapse', val)
      },
      immediate: true,
    },
    sid(newSid) {
      if (newSid) {
        this.restoreChannelState()
        if (!this.isConnected && !this.isConnecting) {
          this.connectToMeeting()
        }
      }
    },
    participants: {
      handler() {
        this.$nextTick(() => this.attachAllVideoTracks())
      },
      deep: true,
    },
  },

  created() {
    this.restoreChannelState()
  },

  mounted() {
    this.connectToMeeting()
    this.initGameOnListener()
    if (window.EventBus) {
      window.EventBus.$on('start-live-demo', this.handleStartLiveDemo)
      window.EventBus.$on('end-live-demo', this.handleEndLiveDemo)
      window.EventBus.$on('teacher-switch-channel-tab', this.handleTeacherSwitchChannelTab)
      window.EventBus.$on('teacher-close-whiteboard', this.handleTeacherCloseWhiteboard)
    }
  },

  beforeDestroy() {
    if (window.EventBus) {
      window.EventBus.$off('start-live-demo', this.handleStartLiveDemo)
      window.EventBus.$off('end-live-demo', this.handleEndLiveDemo)
      window.EventBus.$off('teacher-switch-channel-tab', this.handleTeacherSwitchChannelTab)
      window.EventBus.$off('teacher-close-whiteboard', this.handleTeacherCloseWhiteboard)
    }
    if (App && App.service('game-on')) {
      if (this.gameOnUpdateListener) {
        App.service('game-on').removeListener('patched', this.gameOnUpdateListener)
        App.service('game-on').removeListener('created', this.gameOnUpdateListener)
      }
      if (this.onSocketHelpJoined) {
        App.service('game-on').removeListener('helpJoined', this.onSocketHelpJoined)
      }
      if (this.onSocketHelpLeft) {
        App.service('game-on').removeListener('helpLeft', this.onSocketHelpLeft)
      }
    }
    this.disconnectFromMeeting()
  },

  methods: {
    saveChannelState() {
      try {
        const hasPrivateSessions = Object.keys(this.activePrivateSessions).length > 0
        if (hasPrivateSessions || this.activeTeacherJoinedTeamId != null) {
          const stateToSave = {
            activePrivateSessions: {...this.activePrivateSessions},
            channelStudents: [...this.channelStudents],
            activeWhiteboardStudentIdentity: this.activeWhiteboardStudentIdentity,
            activeTeacherJoinedTeamId: this.activeTeacherJoinedTeamId,
            lastStudentHelpInfo: this.lastStudentHelpInfo,
            cpaDrawUrl: this.cpaDrawUrl,
            timestamp: Date.now(),
          }
          sessionStorage.setItem(this.channelStorageKey, JSON.stringify(stateToSave))
          sessionStorage.setItem('lk_channel_session_active', JSON.stringify(stateToSave))
        } else {
          sessionStorage.removeItem(this.channelStorageKey)
          sessionStorage.removeItem('lk_channel_session_active')
        }
      } catch (err) {}
    },

    restoreChannelState() {
      try {
        let raw = sessionStorage.getItem(this.channelStorageKey)
        if (!raw) {
          raw = sessionStorage.getItem('lk_channel_session_active')
        }
        if (!raw) {
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i)
            if (key && key.startsWith('lk_channel_session_')) {
              raw = sessionStorage.getItem(key)
              if (raw) break
            }
          }
        }
        if (!raw) return
        const data = JSON.parse(raw)
        if (data && typeof data === 'object') {
          if (data.timestamp && Date.now() - data.timestamp > 4 * 60 * 60 * 1000) {
            this.clearChannelState()
            return
          }
          if (data.activePrivateSessions && typeof data.activePrivateSessions === 'object') {
            this.activePrivateSessions = {...data.activePrivateSessions}
          }
          if (data.channelStudents && Array.isArray(data.channelStudents)) {
            this.channelStudents = [...data.channelStudents]
          }
          if (data.activeWhiteboardStudentIdentity) {
            this.activeWhiteboardStudentIdentity = data.activeWhiteboardStudentIdentity
          }
          if (data.activeTeacherJoinedTeamId != null) {
            this.activeTeacherJoinedTeamId = data.activeTeacherJoinedTeamId
          }
          if (data.lastStudentHelpInfo) {
            this.lastStudentHelpInfo = data.lastStudentHelpInfo
          }
          if (data.cpaDrawUrl) {
            this.cpaDrawUrl = data.cpaDrawUrl
          }
          if (Object.keys(this.activePrivateSessions).length > 0 || this.activeTeacherJoinedTeamId != null) {
            this.isMicOn = true
          }
          this.$nextTick(() => {
            this.broadcastChannelStudentsUpdated()
          })
        }
      } catch (err) {}
    },

    clearChannelState() {
      try {
        sessionStorage.removeItem(this.channelStorageKey)
        sessionStorage.removeItem('lk_channel_session_active')
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i)
          if (key && key.startsWith('lk_channel_session_')) {
            sessionStorage.removeItem(key)
          }
        }
        this.activePrivateSessions = {}
        this.channelStudents = []
        this.activeWhiteboardStudentIdentity = ''
        this.activeTeacherJoinedTeamId = null
        this.lastStudentHelpInfo = null
        this.cpaDrawUrl = ''
        this.broadcastChannelStudentsUpdated()
      } catch (err) {}
    },

    broadcastChannelStudentsUpdated() {
      const isInChannel = this.isTeacher ? this.channelStudents && this.channelStudents.length > 0 : true
      const effectiveDrawUrl = isInChannel ? this.cpaDrawUrl : ''
      const effectiveStudentIdentity = isInChannel ? this.activeWhiteboardStudentIdentity : ''
      const payload = {
        channelStudents: [...this.channelStudents],
        activeStudentIdentity: effectiveStudentIdentity,
        cpaDrawUrl: effectiveDrawUrl,
      }
      if (window.EventBus) {
        window.EventBus.$emit('channel-students-updated', payload)
      }
      this.$emit('channel-students-updated', payload)
    },

    broadcastChannelStateSync() {
      if (!this.room || !this.room.localParticipant) return
      const payloadSync = JSON.stringify({
        type: 'CHANNEL_STATE_SYNC',
        activePrivateSessions: {...this.activePrivateSessions},
        teacherIdentity: this.room.localParticipant.identity,
        channelStudents: this.channelStudents.map((s) => ({identity: s.identity, name: s.name})),
        activeStudentIdentity: this.activeWhiteboardStudentIdentity,
      })
      this.room.localParticipant.publishData(new TextEncoder().encode(payloadSync), {reliable: true}).catch(() => {})
    },

    sendStudentHelpInfoToTeacher(teacherIdentity) {
      if (!this.room || !this.room.localParticipant) return
      const targetUid =
        this.uid || (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user._id) || this.room.localParticipant.identity
      const sid = this.sid || (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.sid) || ''
      let slideId = ''
      if (this.$store && this.$store.state) {
        slideId =
          this.$store.state.selectedAnalysisSlide?._id ||
          this.$store.state.currentPage?._id ||
          this.$store.state.question?._id ||
          this.$store.state.currentSlide?._id ||
          (this.$store.state.sessionPages && this.$store.state.sessionPages[this.$store.state.currentPageIndex]?._id) ||
          ''
      }
      let studentDrawKey = (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.drawkey) || ''
      let currentCpaDrawUrl = ''
      const isCpa = this.isCpaQuestion(slideId)
      if (isCpa) {
        currentCpaDrawUrl = this.cpaDrawUrl || ''
        if (!currentCpaDrawUrl || currentCpaDrawUrl === '/draw/') {
          if (targetUid && slideId && sid && studentDrawKey) {
            currentCpaDrawUrl = `/draw/#room=${targetUid}_${slideId}_${sid},${studentDrawKey}`
          } else if (sid && studentDrawKey) {
            currentCpaDrawUrl = `/draw/#room=${sid},${studentDrawKey}`
          }
        }
      }

      const infoPayload = JSON.stringify({
        type: 'STUDENT_HELP_INFO',
        studentIdentity: this.room.localParticipant.identity,
        teacherIdentity: teacherIdentity || '',
        uid: targetUid,
        sid: sid,
        slideId: slideId,
        studentDrawKey: studentDrawKey,
        cpaDrawUrl: currentCpaDrawUrl,
      })
      this.room.localParticipant.publishData(new TextEncoder().encode(infoPayload), {reliable: true}).catch(() => {})
    },

    handleTileClick(p) {
      if (this.isTeacher && !p.isLocal && !this.isParticipantTeacher(p)) {
        if (this.isStudentInAnotherTeacherChannel(p)) {
          return
        }
        if (p.isPrivateSessionPartner) {
          if (this.activeWhiteboardStudentIdentity !== p.identity) {
            if (!this.channelStudents.some((s) => s.identity === p.identity)) {
              this.channelStudents.push({
                identity: p.identity,
                name: p.name || p.identity,
                uid: p.uid || p.identity,
                slideId: p.slideId,
                sid: p.sid || this.sid,
                studentDrawKey: p.studentDrawKey,
                cpaDrawUrl: p.cpaDrawUrl,
              })
            }
            this.handleTeacherSwitchChannelTab(p)
          }
          return
        }
        const fromRaiseHand = !!p.isHandRaised
        if (!this.isTeacherInAnyChannel) {
          this.openHelpModal(p, fromRaiseHand)
        } else {
          this.openChannelSwitchModal(p, fromRaiseHand)
        }
      }
    },

    openChannelSwitchModal(p, fromRaiseHand = false) {
      if (this.isStudentInAnotherTeacherChannel(p)) return
      this.channelSwitchModalStudent = p
      this.isChannelSwitchModalFromRaiseHand = fromRaiseHand
    },

    closeChannelSwitchModal() {
      this.channelSwitchModalStudent = null
      this.isChannelSwitchModalFromRaiseHand = false
    },

    handleOptionSwitchChannel() {
      if (!this.channelSwitchModalStudent) return
      const nextStudent = this.channelSwitchModalStudent
      this.channelSwitchModalStudent = null
      this.isChannelSwitchModalFromRaiseHand = false

      // End channel with all currently connected students
      const currentStudents = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
      if (this.room && this.room.localParticipant) {
        currentStudents.forEach((stId) => {
          const payloadLeave = JSON.stringify({
            type: 'TEACHER_LEFT_STUDENT',
            studentIdentity: stId,
            teacherIdentity: this.room.localParticipant.identity,
          })
          this.room.localParticipant.publishData(new TextEncoder().encode(payloadLeave), {reliable: true}).catch(() => {})
          this.$delete(this.activePrivateSessions, stId)
        })
      } else {
        this.activePrivateSessions = {}
      }

      this.channelStudents = []
      this.activeWhiteboardStudentIdentity = ''
      this.cpaDrawUrl = ''
      this.clearChannelState()

      // Start 1-on-1 session with nextStudent
      this.handleTeacherAcceptHelp(nextStudent)
    },

    handleOptionInviteToCurrentChannel() {
      if (!this.channelSwitchModalStudent) return
      const student = this.channelSwitchModalStudent
      this.channelSwitchModalStudent = null
      this.isChannelSwitchModalFromRaiseHand = false
      this.handleTeacherAddStudentToChannel(student)
    },

    async handleTeacherAddStudentToChannel(p) {
      this.$set(p, 'isHandRaised', false)
      this.scheduleHandRaiseAutoExpiry(p)
      const pUid = p.uid || p.identity || ''
      const pName = p.name || p.identity || ''
      const sid =
        p.sessionId || this.sid || (this.$store && this.$store.state.session?.sid) || (window.App && window.App.get && window.App.get('session')?.sid) || ''

      let slideId = p.slideId || p.questionId || ''
      if (!slideId && this.$store && this.$store.state) {
        slideId =
          this.$store.state.selectedAnalysisSlide?._id ||
          this.$store.state.currentPage?._id ||
          this.$store.state.question?._id ||
          this.$store.state.currentSlide?._id ||
          (this.$store.state.sessionPages && this.$store.state.sessionPages[this.$store.state.currentPageIndex]?._id) ||
          ''
      }

      let studentDrawKey = p.studentDrawKey || (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.drawkey) || ''

      let resolvedDrawUrl = p.cpaDrawUrl || ''
      if (!resolvedDrawUrl && pUid && slideId && sid && studentDrawKey) {
        resolvedDrawUrl = `/draw/#room=${pUid}_${slideId}_${sid},${studentDrawKey}`
      }

      // Ensure existing students in activePrivateSessions are recorded in channelStudents
      const existingKeys = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL' && k !== p.identity)
      existingKeys.forEach((key) => {
        if (!this.channelStudents.some((s) => s.identity === key)) {
          const existingP = this.participants.find((item) => item.identity === key || item.uid === key)
          const existingUid = (existingP && (existingP.uid || existingP.identity)) || key
          const existingStudentDrawKey = (existingP && existingP.studentDrawKey) || studentDrawKey
          const existingSlideId = (existingP && existingP.slideId) || slideId
          // Construct per-student draw URL from their own uid — do NOT fall back to this.cpaDrawUrl
          // which belongs to the currently-active student's whiteboard
          let existingCpaDrawUrl = existingP && existingP.cpaDrawUrl ? existingP.cpaDrawUrl : ''
          if (!existingCpaDrawUrl && existingUid && existingSlideId && sid && existingStudentDrawKey) {
            existingCpaDrawUrl = `/draw/#room=${existingUid}_${existingSlideId}_${sid},${existingStudentDrawKey}`
          }
          this.channelStudents.push({
            identity: key,
            name: existingP ? existingP.name : key,
            uid: existingUid,
            slideId: existingSlideId,
            sid: sid,
            studentDrawKey: existingStudentDrawKey,
            cpaDrawUrl: existingCpaDrawUrl,
          })
        }
      })
      if (!this.activeWhiteboardStudentIdentity && this.channelStudents.length > 0) {
        this.activeWhiteboardStudentIdentity = this.channelStudents[0].identity
      }

      // Add new student to channelStudents
      let existingStudentEntry = this.channelStudents.find((s) => s.identity === p.identity)
      if (!existingStudentEntry) {
        existingStudentEntry = {
          identity: p.identity,
          name: pName,
          uid: pUid,
          slideId: slideId,
          sid: sid,
          studentDrawKey: studentDrawKey,
          cpaDrawUrl: resolvedDrawUrl,
        }
        this.channelStudents.push(existingStudentEntry)
      }

      this.$set(this.activePrivateSessions, p.identity, this.room.localParticipant.identity)

      if (this.room && this.room.localParticipant) {
        const payloadHand = JSON.stringify({
          type: 'RAISE_HAND',
          identity: p.identity,
          name: p.name,
          sid: p.sid,
          isHandRaised: false,
        })
        this.room.localParticipant.publishData(new TextEncoder().encode(payloadHand), {reliable: true}).catch(() => {})

        const payloadAccept = JSON.stringify({
          type: 'TEACHER_ACCEPTED_STUDENT',
          studentIdentity: p.identity,
          teacherIdentity: this.room.localParticipant.identity,
          isGroupChannel: true,
        })
        this.room.localParticipant.publishData(new TextEncoder().encode(payloadAccept), {reliable: true}).catch(() => {})

        this.broadcastChannelStateSync()

        // Broadcast current active tab to students
        const activeStudent = this.channelStudents.find((s) => s.identity === this.activeWhiteboardStudentIdentity) || this.channelStudents[0]
        if (activeStudent) {
          const payloadTab = JSON.stringify({
            type: 'CHANNEL_TAB_SWITCH',
            activeStudentIdentity: activeStudent.identity,
            cpaDrawUrl: activeStudent.cpaDrawUrl || this.cpaDrawUrl,
            studentName: activeStudent.name || '',
            teacherIdentity: this.room.localParticipant.identity,
          })
          this.room.localParticipant.publishData(new TextEncoder().encode(payloadTab), {reliable: true}).catch(() => {})
        }
      }

      this.saveChannelState()
      this.syncAllParticipantsTeamState()
      this.broadcastChannelStudentsUpdated()
      this.updateParticipantState(p)
    },

    handleTeacherSwitchChannelTab(tab) {
      if (!tab) return
      const targetIdentity = tab.identity || tab
      this.activeWhiteboardStudentIdentity = targetIdentity
      let found = this.channelStudents.find((s) => s.identity === targetIdentity)
      if (!found) {
        found = {
          identity: targetIdentity,
          name: tab.name || targetIdentity,
          uid: tab.uid || targetIdentity,
          slideId: tab.slideId,
          sid: tab.sid || this.sid,
          studentDrawKey: tab.studentDrawKey,
          cpaDrawUrl: tab.cpaDrawUrl,
        }
        this.channelStudents.push(found)
      }

      let slideId = tab.slideId || found.slideId || ''
      if (!slideId && this.$store && this.$store.state) {
        slideId = this.$store.state.selectedAnalysisSlide?._id || this.$store.state.currentPage?._id || ''
      }
      const isCpa =
        tab && typeof tab.isCpa === 'boolean'
          ? tab.isCpa
          : found && typeof found.isCpa === 'boolean'
          ? found.isCpa
          : this.isCpaQuestion(slideId)

      if (isCpa) {
        if (tab.cpaDrawUrl && tab.cpaDrawUrl !== '/draw/') {
          found.cpaDrawUrl = tab.cpaDrawUrl
          this.cpaDrawUrl = tab.cpaDrawUrl
        } else if (found.cpaDrawUrl && found.cpaDrawUrl !== '/draw/') {
          this.cpaDrawUrl = found.cpaDrawUrl
        } else {
          const targetUid = tab.uid || found.uid || targetIdentity
          const targetSid = tab.sid || found.sid || this.sid || (this.$store && this.$store.state.session?.sid) || ''
          const targetDrawKey = tab.studentDrawKey || found.studentDrawKey || (this.$store && this.$store.state.session?.drawkey) || ''
          if (targetUid && slideId && targetSid && targetDrawKey) {
            this.cpaDrawUrl = `/draw/#room=${targetUid}_${slideId}_${targetSid},${targetDrawKey}`
            found.cpaDrawUrl = this.cpaDrawUrl
          }
        }
      } else {
        this.cpaDrawUrl = ''
        found.cpaDrawUrl = ''
      }

      this.saveChannelState()
      this.broadcastChannelStudentsUpdated()

      const switchHelpData = {
        identity: targetIdentity,
        uid: (found && found.uid) || targetIdentity,
        slideId: slideId,
        pageIndex: tab.pageIndex != null ? tab.pageIndex : found.pageIndex != null ? found.pageIndex : null,
        isCpa,
        cpaDrawUrl: this.cpaDrawUrl,
        participant: tab,
        channelStudents: [...this.channelStudents],
        activeStudentIdentity: targetIdentity,
      }
      this.$emit('accepted-student-help', switchHelpData)
      if (window.EventBus) {
        window.EventBus.$emit('accepted-student-help', switchHelpData)
      }

      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'CHANNEL_TAB_SWITCH',
          activeStudentIdentity: targetIdentity,
          cpaDrawUrl: this.cpaDrawUrl,
          studentName: tab.name || (found && found.name) || '',
          teacherIdentity: this.room.localParticipant.identity,
        })
        this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})
      }
    },

    openHelpModal(p, fromRaiseHand = false) {
      if (this.isStudentInAnotherTeacherChannel(p)) return
      this.helpModalStudent = p
      this.isHelpModalFromRaiseHand = fromRaiseHand
    },

    closeHelpModal() {
      this.helpModalStudent = null
      this.isHelpModalFromRaiseHand = false
    },

    confirmHelpStudent() {
      if (this.helpModalStudent) {
        const student = this.helpModalStudent
        this.helpModalStudent = null
        this.isHelpModalFromRaiseHand = false
        if (this.isStudentInAnotherTeacherChannel(student)) return
        this.handleTeacherAcceptHelp(student)
      }
    },

    openLeaveModal(p) {
      this.leaveModalStudent = p
    },

    closeLeaveModal() {
      this.leaveModalStudent = null
    },

    closeTeacherBusyModal() {
      this.teacherBusyModalVisible = false
    },

    confirmLeaveChannel() {
      if (this.leaveModalStudent) {
        const student = this.leaveModalStudent
        this.leaveModalStudent = null
        if (this.isTeacher) {
          this.handleTeacherLeaveHelp(student)
        } else {
          this.handleStudentLeavePeerHelp(student)
        }
      }
    },

    async initGameOnListener() {
      if (!this.sid || !App || !App.service('game-on')) return
      try {
        const res = await App.service('game-on').find({
          query: {sid: this.sid, $sort: {createdAt: -1}, $limit: 1},
        })
        if (res && res.data && res.data.length > 0) {
          this.gameOnRecord = res.data[0]
          this.syncAllParticipantsTeamState()
          // If game is already active when we join, mute teacher mic
          if (this.gameOnRecord.status === 1 && this.room?.localParticipant) {
            this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
            this.isMicOn = false
            this.updateParticipantState(this.room.localParticipant)
          }
        }
      } catch (e) {}

      this.gameOnUpdateListener = (data) => {
        if (data && data.sid === this.sid) {
          const prevStatus = this.gameOnRecord ? this.gameOnRecord.status : 0
          this.gameOnRecord = data
          this.syncAllParticipantsTeamState()

          // When game transitions to active: mute teacher mic and notify all students
          if (data.status === 1 && prevStatus !== 1) {
            if (this.room && this.room.localParticipant) {
              this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
              this.isMicOn = false
              this.updateParticipantState(this.room.localParticipant)
              const payload = JSON.stringify({type: 'GAME_ON_START', gameOnRecord: data})
              this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})
            }
          } else if (data.status !== 1 && prevStatus === 1) {
            if (this.room && this.room.localParticipant) {
              const payload = JSON.stringify({type: 'GAME_ON_STOP', gameOnRecord: data})
              this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})
            }
          }
        }
      }

      App.service('game-on').on('patched', this.gameOnUpdateListener)
      App.service('game-on').on('created', this.gameOnUpdateListener)

      this.onSocketHelpJoined = (data) => {
        const myUid = String(this.uid || this.participantIdentity || '')
        const myTeamId = this.getTeamIdForUser(this.uid) || null
        if (data && myTeamId && String(data.teamId) === String(myTeamId)) {
          if (String(data.requesterUid) === myUid || String(data.joinerUid) === myUid) {
            this.isPeerHelpActive = true
            const partnerUid = String(data.requesterUid) === myUid ? String(data.joinerUid) : String(data.requesterUid)
            this.$set(this.activePrivateSessions, data.requesterUid, data.joinerUid)
            this.$set(this.activePrivateSessions, myUid, partnerUid)
            this.saveChannelState()
            this.syncAllParticipantsTeamState()
          }
        }
      }
      this.onSocketHelpLeft = (data) => {
        const myTeamId = this.getTeamIdForUser(this.uid) || null
        if (data && myTeamId && String(data.teamId) === String(myTeamId)) {
          this.isPeerHelpActive = false
          if (data.requesterUid) this.$delete(this.activePrivateSessions, data.requesterUid)
          if (data.joinerUid) this.$delete(this.activePrivateSessions, data.joinerUid)
          this.saveChannelState()
          this.syncAllParticipantsTeamState()
        }
      }
      App.service('game-on').on('helpJoined', this.onSocketHelpJoined)
      App.service('game-on').on('helpLeft', this.onSocketHelpLeft)
    },

    getTeamIdForUser(userUid) {
      if (!this.gameOnRecord || this.gameOnRecord.status !== 1) return null
      const uidStr = String(userUid || '')
      if (!uidStr) return null

      const checkMatch = (m) => {
        const mUid = String(m.uid || m.originalMemberData?.uid || '')
        const mId = String(m._id || m.id || '')
        return (mUid && uidStr === mUid) || (mId && uidStr === mId)
      }

      if (this.gameOnRecord.teams) {
        for (const team of this.gameOnRecord.teams) {
          const members = team.members || []
          for (const m of members) {
            if (checkMatch(m)) return String(team.id)
          }
        }
      }
      if (this.gameOnRecord.members) {
        for (const m of this.gameOnRecord.members) {
          if (checkMatch(m)) return String(m.teamId)
        }
      }
      return null
    },

    isTeammate(p) {
      if (p.isLocal) return true
      if (!this.gameOnRecord || this.gameOnRecord.status !== 1) return true

      if (this.isTeacher && this.activeTeacherJoinedTeamId != null) {
        const pUid = p.uid || p.identity || ''
        const pTeamId = this.getTeamIdForUser(pUid)
        if (pTeamId != null && String(pTeamId) === String(this.activeTeacherJoinedTeamId)) {
          return true
        }
      }

      const myUid = (this.room && this.room.localParticipant ? this.room.localParticipant.identity : '') || this.uid || window.currentUserId || ''
      const myTeamId = this.getTeamIdForUser(myUid)

      const pUid = p.uid || p.identity || ''
      const pTeamId = this.getTeamIdForUser(pUid)

      if (myTeamId != null && pTeamId != null && String(myTeamId) === String(pTeamId)) {
        return true
      }
      return false
    },

    shouldMuteParticipantAudio(p) {
      if (p.isLocal) return false
      const pIdent = (p.identity || '').toLowerCase()
      const pName = (p.name || '').toLowerCase()
      if (pIdent.includes('_demo') || pName.includes('_demo') || pIdent.includes('recorder') || pName.includes('recorder')) {
        return true
      }
      const isGameOn = this.gameOnRecord && this.gameOnRecord.status === 1
      const teammate = this.isTeammate(p)

      if (this.activePrivateSessions['ALL']) {
        return false
      }

      const myParticipant = this.room?.localParticipant
      const amITeacher = this.isTeacher || this.isParticipantTeacher(myParticipant)
      const amIInPrivateSession = amITeacher
        ? this.isTeacherInAnyChannel
        : this.isStudentInTeacherChannel || this.isStudentInPeerChannel || this.isPeerHelpActive
      const areWePartners = this.isParticipantPartnerWithLocal(p)
      const pIsInPrivateSession = this.isParticipantInAnyPrivateChannel(p)

      if (amIInPrivateSession) {
        return !areWePartners
      } else if (pIsInPrivateSession) {
        return true
      } else if (isGameOn) {
        return !teammate
      }

      return false
    },

    syncTeamChannelAudio() {
      if (!this.room) return
      this.room.remoteParticipants.forEach((p) => {
        const shouldMuteAudio = this.shouldMuteParticipantAudio(p)
        p.audioTrackPublications.forEach((pub) => {
          if (pub.track) {
            const audioEls = []
            if (pub.track.attachedElements && pub.track.attachedElements.length > 0) {
              audioEls.push(...pub.track.attachedElements)
            }
            const elById = document.getElementById(`lk-sidebar-audio-${pub.track.sid}`) || document.getElementById(`lk-audio-${pub.track.sid}`)
            if (elById && !audioEls.includes(elById)) {
              audioEls.push(elById)
            }
            audioEls.forEach((audioEl) => {
              audioEl.muted = shouldMuteAudio
              audioEl.volume = shouldMuteAudio ? 0 : 1.0
              if (shouldMuteAudio) {
                audioEl.pause()
              } else if (audioEl.paused) {
                audioEl.play().catch(() => {})
              }
            })
          }
        })
      })
    },

    syncAllParticipantsTeamState() {
      if (!this.room) return
      if (this.room.localParticipant) {
        this.updateParticipantState(this.room.localParticipant)
      }
      this.room.remoteParticipants.forEach((p) => {
        this.updateParticipantState(p)
      })
      this.syncTeamChannelAudio()
      // Delayed re-sync to catch race conditions where track events
      // arrive before data channel messages update activePrivateSessions
      clearTimeout(this._syncTimer)
      this._syncTimer = setTimeout(() => {
        this.syncTeamChannelAudio()
      }, 300)
    },

    getAvatarColor(name) {
      return '#434655'
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length > 1 && parts[0] && parts[parts.length - 1]) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.trim().substring(0, 2).toUpperCase()
    },

    isCpaQuestion(targetSlideOrQuestion) {
      if (!targetSlideOrQuestion) return false
      if (typeof targetSlideOrQuestion === 'object') {
        if (targetSlideOrQuestion.hasOwnProperty('isCpa')) {
          return !!targetSlideOrQuestion.isCpa
        }
        if (targetSlideOrQuestion.cpa?.outcome || targetSlideOrQuestion.cpaOutcome) {
          return true
        }
      }
      const pageId =
        typeof targetSlideOrQuestion === 'object'
          ? targetSlideOrQuestion._id || targetSlideOrQuestion.id || targetSlideOrQuestion.page || targetSlideOrQuestion.pageId
          : targetSlideOrQuestion

      const questions =
        (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.questions) ||
        (this.$store && this.$store.state && this.$store.state.teacher && this.$store.state.teacher.rooms && this.$store.state.teacher.rooms.questions) ||
        (this.gameOnRecord && this.gameOnRecord.questions) ||
        []
      const found = questions.find((q) => q._id === pageId || q.id === pageId || q.page === pageId || q.pageId === pageId)
      if (found) {
        return !!(found.cpa?.outcome || found.isCpa || found.cpaOutcome)
      }

      const pages =
        (this.$store && this.$store.state && this.$store.state.sessionPages) ||
        (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.pages) ||
        []
      const foundPage = pages.find((p) => p._id === pageId || p.id === pageId || p.page === pageId || p.pageId === pageId)
      if (foundPage) {
        return !!(foundPage.cpa?.outcome || foundPage.isCpa || foundPage.cpaOutcome)
      }

      return false
    },

    isParticipantTeacher(p) {
      if (!p) return false
      if (p.isLocal && this.isTeacher) return true
      if (p.isTeacher) return true

      const pUid = String(p.uid || p.identity || '').trim()
      const pName = String(p.name || '')
        .trim()
        .toLowerCase()

      const session = this.$store?.state?.session || (window.App && window.App.get && window.App.get('session'))
      if (session?.uid && String(session.uid) === pUid) return true
      if (session?.user?._id && String(session.user._id) === pUid) return true
      if (session?.owner && String(session.owner) === pUid) return true
      if (session?.teacherId && String(session.teacherId) === pUid) return true

      const teachers = this.$store?.state?.teacher?.teachers || window.teachers || []
      if (Array.isArray(teachers) && teachers.length > 0) {
        const matched = teachers.some((t) => {
          const tId = String(t._id || t.id || t.uid || '').trim()
          const tName = String(Array.isArray(t.name) ? t.name.join(' ') : t.name || '')
            .trim()
            .toLowerCase()
          const tEmail = String(t.email || '')
            .trim()
            .toLowerCase()
          return (pUid && tId && pUid === tId) || (pName && tName && (pName === tName || pName.includes(tName))) || (pName && tEmail && pName === tEmail)
        })
        if (matched) return true
      }

      if (pName === 'teacher' || pName.startsWith('teacher ') || pName.endsWith(' (teacher)') || pUid.toLowerCase().includes('teacher')) {
        return true
      }

      return false
    },

    isIdentityTeacher(id) {
      if (!id) return false
      if (id === 'ALL') return true
      const idStr = String(id).trim()
      if (this.isTeacher) {
        const myId = String((this.room?.localParticipant && this.room.localParticipant.identity) || this.participantIdentity || this.uid || '').trim()
        if (idStr === myId) return true
      }
      const p = this.participants.find((item) => String(item.identity || '').trim() === idStr || String(item.uid || '').trim() === idStr)
      if (p && this.isParticipantTeacher(p)) return true

      const session = this.$store?.state?.session || (window.App && window.App.get && window.App.get('session'))
      if (session?.uid && String(session.uid) === idStr) return true
      if (session?.user?._id && String(session.user._id) === idStr) return true
      if (session?.owner && String(session.owner) === idStr) return true
      if (session?.teacherId && String(session.teacherId) === idStr) return true

      const teachers = this.$store?.state?.teacher?.teachers || window.teachers || []
      if (Array.isArray(teachers) && teachers.length > 0) {
        const matched = teachers.some((t) => {
          const tId = String(t._id || t.id || t.uid || '').trim()
          return tId && tId === idStr
        })
        if (matched) return true
      }

      if (idStr.toLowerCase().includes('teacher')) return true
      return false
    },

    getParticipantIds(p) {
      if (!p) return []
      const ids = []
      if (typeof p === 'string') {
        if (p.trim()) ids.push(p.trim())
        return ids
      }
      if (p.identity) ids.push(String(p.identity).trim())
      if (p.uid) ids.push(String(p.uid).trim())
      if (p.sid) ids.push(String(p.sid).trim())
      if (p.isLocal || p === this.room?.localParticipant) {
        if (this.uid) ids.push(String(this.uid).trim())
        if (this.participantIdentity) ids.push(String(this.participantIdentity).trim())
        if (this.room?.localParticipant?.identity) ids.push(String(this.room.localParticipant.identity).trim())
      }
      return [...new Set(ids.filter(Boolean))]
    },

    getParticipantPartnerIdentity(p) {
      if (!p) return null
      if (this.activePrivateSessions['ALL']) return 'ALL'
      const ids = this.getParticipantIds(p)
      if (ids.length === 0) return null

      for (const id of ids) {
        if (this.activePrivateSessions[id]) return this.activePrivateSessions[id]
      }

      const foundKey = Object.keys(this.activePrivateSessions).find((k) => {
        const val = String(this.activePrivateSessions[k] || '').trim()
        return ids.includes(val)
      })
      return foundKey || null
    },

    isStudentInAnotherTeacherChannel(p) {
      if (!p || this.isParticipantTeacher(p) || p.isLocal) return false
      if (p.isPrivateSessionPartner) return false
      const pIds = this.getParticipantIds(p)
      const activeStudentIds = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
      const matchedStudentId = activeStudentIds.find((sId) => {
        return pIds.some((id) => id === sId || sId.includes(id))
      })
      const partner = matchedStudentId ? String(this.activePrivateSessions[matchedStudentId]).trim() : this.getParticipantPartnerIdentity(p)
      if (!partner) return false
      if (partner === 'ALL') {
        return !this.isTeacherInAnyChannel
      }
      if (!this.isIdentityTeacher(partner)) return false
      const myParticipant = this.room?.localParticipant
      const myIds = this.getParticipantIds(myParticipant)
      return !myIds.some((id) => id === partner || partner.includes(id))
    },

    isParticipantInAnyPrivateChannel(p) {
      if (!p) return false
      if (this.activePrivateSessions['ALL']) return true
      const isPTeacher = this.isParticipantTeacher(p)
      if (isPTeacher) {
        if (this.activeTeacherJoinedTeamId != null) return true
        const activeStudentIds = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
        const pIds = this.getParticipantIds(p)
        return activeStudentIds.some((sId) => {
          const partner = String(this.activePrivateSessions[sId] || '').trim()
          return pIds.some((id) => id === partner || partner.includes(id) || id === sId || sId.includes(id))
        })
      } else {
        const pPartner = this.getParticipantPartnerIdentity(p)
        return pPartner != null
      }
    },

    isParticipantPartnerWithLocal(p) {
      if (!p || p.isLocal) return false
      if (this.activePrivateSessions['ALL']) return true

      const myParticipant = this.room?.localParticipant
      const amITeacher = this.isTeacher || this.isParticipantTeacher(myParticipant)
      const isPTeacher = this.isParticipantTeacher(p)

      if (amITeacher) {
        if (this.activeTeacherJoinedTeamId != null) {
          return this.isTeammate(p)
        }
        const myIds = this.getParticipantIds(myParticipant)
        const pIds = this.getParticipantIds(p)

        const activeStudentIds = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
        return activeStudentIds.some((sId) => {
          const partner = String(this.activePrivateSessions[sId] || '').trim()
          const isStudentMatch = pIds.some((id) => id === sId || sId.includes(id) || id === partner || partner.includes(id))
          const isTeacherMatch = myIds.some((id) => id === partner || partner.includes(id) || id === sId || sId.includes(id))
          return isStudentMatch && isTeacherMatch
        })
      } else {
        const myIds = this.getParticipantIds(myParticipant)
        const pIds = this.getParticipantIds(p)
        const myPartner = this.getParticipantPartnerIdentity(myParticipant)

        if (myPartner && this.isIdentityTeacher(myPartner)) {
          return isPTeacher && pIds.some((id) => id === myPartner || myPartner.includes(id) || id.includes(myPartner))
        }

        if (isPTeacher) return false

        if (myPartner && pIds.some((id) => id === myPartner || myPartner.includes(id) || id.includes(myPartner))) {
          return true
        }

        const pPartner = this.getParticipantPartnerIdentity(p)
        if (pPartner) {
          if (this.isIdentityTeacher(pPartner)) {
            return false
          }
          if (myIds.some((id) => id === pPartner || pPartner.includes(id) || id.includes(pPartner))) {
            return true
          }
        }

        return Object.keys(this.activePrivateSessions).some((k) => {
          const val = String(this.activePrivateSessions[k] || '').trim()
          if (this.isIdentityTeacher(k) || this.isIdentityTeacher(val)) return false
          return (
            (myIds.some((id) => id === k || k.includes(id)) && pIds.some((id) => id === val || val.includes(id))) ||
            (pIds.some((id) => id === k || k.includes(id)) && myIds.some((id) => id === val || val.includes(id)))
          )
        })
      }
    },

    attachAllVideoTracks() {
      this.participants.forEach((p) => {
        if (p.isVideoOn && p.videoTrack) {
          const refKey = 'video_' + p.sid
          const refs = this.$refs[refKey]
          const el = Array.isArray(refs) ? refs[0] : refs
          if (el && p.videoTrack) {
            p.videoTrack.attach(el)
          }
        }
      })
    },

    updateParticipantState(p) {
      if (!p) return
      const rawName = p.name || ''
      const pId = p.identity || ''
      if (
        rawName === 'Unknown' ||
        rawName.includes('_demo') ||
        rawName.includes('_Recorder') ||
        rawName.includes('recorder-bot') ||
        pId.includes('_demo') ||
        pId.includes('_Recorder') ||
        pId.includes('recorder-bot')
      ) {
        return
      }

      const cameraPub = p.getTrackPublication(Track.Source.Camera)
      const micPub = p.getTrackPublication(Track.Source.Microphone)

      const isGameOn = this.gameOnRecord && this.gameOnRecord.status === 1
      const isTeammate = this.isTeammate(p)

      const roomNameClean = (this.roomName || '').toLowerCase()
      const inMain =
        roomNameClean === 'mainroom' ||
        roomNameClean === 'main session' ||
        (this.sid && (this.roomName === `MainRoom_${this.sid}` || this.roomName === `${this.sid}__MainRoom`))

      const myParticipant = this.room?.localParticipant
      const amITeacher = this.isTeacher || this.isParticipantTeacher(myParticipant)
      const amIInPrivateSession = amITeacher
        ? this.isTeacherInAnyChannel
        : this.isStudentInTeacherChannel || this.isStudentInPeerChannel || this.isPeerHelpActive
      const areWePartners = this.isParticipantPartnerWithLocal(p)
      const pIsInPrivateSession = this.isParticipantInAnyPrivateChannel(p)

      let isBlurred = false
      let isChannelMuted = false

      if (p.isLocal) {
        isBlurred = false
        isChannelMuted = false
      } else if (amIInPrivateSession) {
        isBlurred = !areWePartners
        isChannelMuted = !areWePartners
      } else if (pIsInPrivateSession) {
        isBlurred = true
        isChannelMuted = true
      } else {
        isBlurred = isGameOn ? !isTeammate : inMain
        if (!isGameOn && p.isMicrophoneEnabled) {
          isBlurred = false
        }
        isChannelMuted = isGameOn && !isTeammate
      }

      const pName = p.name || p.identity || 'Unknown'

      const localMicActive = p.isLocal ? this.isMicOn || p.isMicrophoneEnabled : p.isMicrophoneEnabled
      const idx = this.participants.findIndex(
        (item) =>
          item.sid === p.sid ||
          (item.identity && p.identity && item.identity === p.identity) ||
          (item.name && pName && item.name === pName && item.isLocal === p.isLocal)
      )
      const existing = idx !== -1 ? this.participants[idx] : null

      const state = {
        sid: p.sid || existing?.sid || '',
        identity: p.identity || existing?.identity || (p.isLocal ? this.participantIdentity || this.uid || '' : ''),
        name: pName,
        isLocal: p.isLocal,
        videoTrack: cameraPub?.track || null,
        audioTrack: micPub?.track || null,
        isMicOn: localMicActive,
        isVideoOn: p.isCameraEnabled,
        audioLevel: localMicActive ? p.audioLevel || 0 : 0,
        isBlurred,
        isChannelMuted,
        isPrivateSessionPartner: areWePartners,
        isInPrivateChannel: pIsInPrivateSession,
        isTeacher: this.isParticipantTeacher(p) || p.isTeacher || false,
        isHandRaised: existing ? !!existing.isHandRaised : false,
        uid: existing?.uid || p.uid || (p.isLocal ? this.uid : ''),
        slideId: existing?.slideId || '',
        questionId: existing?.questionId || '',
        studentDrawKey: existing?.studentDrawKey || '',
        cpaDrawUrl: existing?.cpaDrawUrl || '',
        sessionId: existing?.sessionId || '',
      }

      if (p.isLocal) {
        if (p.isMicrophoneEnabled) {
          this.isMicOn = true
        }
        this.isVideoOn = p.isCameraEnabled
      }

      if (idx !== -1) {
        this.$set(this.participants, idx, state)
      } else {
        this.participants.push(state)
      }

      for (let i = this.participants.length - 1; i >= 0; i--) {
        const item = this.participants[i]
        if (
          item.sid !== p.sid &&
          ((item.identity && p.identity && item.identity === p.identity) || (item.name && pName && item.name === pName && item.isLocal === p.isLocal))
        ) {
          this.participants.splice(i, 1)
        }
      }

      this.syncTeamChannelAudio()
    },

    removeParticipant(p) {
      const pName = p.name || p.identity || 'Unknown'
      const idx = this.participants.findIndex(
        (item) =>
          item.sid === p.sid ||
          (item.identity && p.identity && item.identity === p.identity) ||
          (item.name && pName && item.name === pName && item.isLocal === p.isLocal)
      )
      if (idx !== -1) {
        this.participants.splice(idx, 1)
      }
    },

    setupRoomListeners(rm) {
      rm.on(RoomEvent.ParticipantConnected, (p) => {
        const pIdent = (p.identity || '').toLowerCase()
        const pName = (p.name || '').toLowerCase()
        if (pIdent.includes('_demo') || pName.includes('_demo') || pIdent.includes('recorder') || pName.includes('recorder')) {
          return
        }
        if (this.isTeacher && this.room?.localParticipant) {
          const payload = JSON.stringify({
            type: 'TEACHER_ANNOUNCE',
            teacherIdentity: this.room.localParticipant.identity,
            teacherName: this.userName,
          })
          this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})

          // If teacher is currently in private session with this student (or group demo)
          const pIds = this.getParticipantIds(p)
          const myIds = this.getParticipantIds(this.room.localParticipant)
          const isStudentInMySession =
            this.activePrivateSessions['ALL'] != null ||
            Object.keys(this.activePrivateSessions).some((sId) => {
              if (sId === 'ALL') return false
              const partner = String(this.activePrivateSessions[sId] || '').trim()
              const isThisStudent = pIds.some((id) => id === sId || sId.includes(id))
              const isMyStudent = myIds.some((id) => id === partner || partner.includes(id))
              return isThisStudent && isMyStudent
            })
          if (isStudentInMySession) {
            const pUid = p.identity || p.uid || ''
            const payloadAccept = JSON.stringify({
              type: 'TEACHER_ACCEPTED_STUDENT',
              studentIdentity: this.activePrivateSessions['ALL'] != null ? 'ALL' : pUid,
              teacherIdentity: this.room.localParticipant.identity,
            })
            this.room.localParticipant.publishData(new TextEncoder().encode(payloadAccept), {reliable: true}).catch(() => {})
          }
          if (Object.keys(this.activePrivateSessions).length > 0 || this.activeTeacherJoinedTeamId != null) {
            const syncPayload = JSON.stringify({
              type: 'CHANNEL_STATE_SYNC',
              activePrivateSessions: {...this.activePrivateSessions},
              activeTeacherJoinedTeamId: this.activeTeacherJoinedTeamId,
            })
            this.room.localParticipant.publishData(new TextEncoder().encode(syncPayload), {reliable: true}).catch(() => {})
          }
        } else if (!this.isTeacher && this.room?.localParticipant) {
          const myUid = this.participantIdentity
          const myIdent = this.room.localParticipant.identity
          const teacherIdent = this.activePrivateSessions['ALL'] || this.activePrivateSessions[myUid] || this.activePrivateSessions[myIdent]
          const pIds = this.getParticipantIds(p)
          if (teacherIdent && (teacherIdent === 'ALL' || pIds.some((id) => id === teacherIdent || teacherIdent.includes(id)))) {
            this.sendStudentHelpInfoToTeacher(p.identity)
          }
        }
        this.updateParticipantState(p)
      })
        .on(RoomEvent.ParticipantDisconnected, (p) => {
          this.removeParticipant(p)
        })
        .on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          if (track.kind === Track.Kind.Audio) {
            const pIdent = (participant.identity || '').toLowerCase()
            const pName = (participant.name || '').toLowerCase()
            if (pIdent.includes('_demo') || pName.includes('_demo') || pIdent.includes('recorder') || pName.includes('recorder')) {
              return
            }
            const shouldMute = this.shouldMuteParticipantAudio(participant)
            const element = track.attach()
            element.id = `lk-sidebar-audio-${track.sid}`
            element.muted = shouldMute
            element.volume = shouldMute ? 0 : 1.0
            element.autoplay = true
            document.body.appendChild(element)
            if (!shouldMute) {
              element.play().catch(() => {
                const resumeAudio = () => {
                  element.play().catch(() => {})
                  document.removeEventListener('click', resumeAudio)
                  document.removeEventListener('touchstart', resumeAudio)
                }
                document.addEventListener('click', resumeAudio, {once: true})
                document.addEventListener('touchstart', resumeAudio, {once: true})
              })
            }
          }
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
          track.detach().forEach((el) => el.remove())
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.TrackUnpublished, (publication, participant) => {
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.LocalTrackPublished, (publication, participant) => {
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.TrackMuted, (publication, participant) => {
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.TrackUnmuted, (publication, participant) => {
          this.updateParticipantState(participant)
          this.syncTeamChannelAudio()
        })
        .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          if (speakers.length > 0) {
            this.activeSpeakerSid = speakers[0].sid
          }
          this.syncTeamChannelAudio()
        })
        .on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const text = new TextDecoder().decode(payload)
            const msg = JSON.parse(text)
            if (msg.type === 'REQUEST_CHANNEL_SYNC') {
              if (
                this.isTeacher &&
                this.room?.localParticipant &&
                (Object.keys(this.activePrivateSessions).length > 0 || this.activeTeacherJoinedTeamId != null)
              ) {
                const syncPayload = JSON.stringify({
                  type: 'CHANNEL_STATE_SYNC',
                  activePrivateSessions: {...this.activePrivateSessions},
                  activeTeacherJoinedTeamId: this.activeTeacherJoinedTeamId,
                  targetRequester: msg.requesterIdentity,
                })
                this.room.localParticipant.publishData(new TextEncoder().encode(syncPayload), {reliable: true}).catch(() => {})
              }
            } else if (msg.type === 'PEER_HELP_ACCEPTED') {
              const myUid = (this.room && this.room.localParticipant ? this.room.localParticipant.identity : '') || this.participantIdentity || this.uid || ''
              const myIdent = (this.room && this.room.localParticipant && this.room.localParticipant.identity) || myUid

              this.$set(this.activePrivateSessions, msg.requesterIdentity, msg.helperIdentity)
              if (msg.requesterUid && msg.helperUid) this.$set(this.activePrivateSessions, msg.requesterUid, msg.helperUid)
              if (msg.requesterIdentity && msg.helperUid) this.$set(this.activePrivateSessions, msg.requesterIdentity, msg.helperUid)
              if (msg.requesterUid && msg.helperIdentity) this.$set(this.activePrivateSessions, msg.requesterUid, msg.helperIdentity)
              this.saveChannelState()
              this.syncAllParticipantsTeamState()

              const isRequester = msg.requesterIdentity === myIdent || msg.requesterIdentity === myUid || (msg.requesterUid && msg.requesterUid === myUid)
              const isHelper = msg.helperIdentity === myIdent || msg.helperIdentity === myUid || (msg.helperUid && msg.helperUid === myUid)

              if (isRequester || isHelper) {
                this.isPeerHelpActive = true
                if (this.room && this.room.localParticipant) {
                  this.isMicOn = true
                  this.room.localParticipant
                    .setMicrophoneEnabled(true)
                    .then(() => {
                      this.isMicOn = true
                      this.updateParticipantState(this.room.localParticipant)
                    })
                    .catch((e) => {
                      console.warn('[EmbeddedMeeting] Mic auto-enable error:', e)
                    })
                  this.updateParticipantState(this.room.localParticipant)
                }
              }
            } else if (msg.type === 'PEER_HELP_LEFT') {
              Object.keys(this.activePrivateSessions).forEach((k) => {
                if (
                  k === msg.studentA ||
                  k === msg.studentB ||
                  this.activePrivateSessions[k] === msg.studentA ||
                  this.activePrivateSessions[k] === msg.studentB
                ) {
                  this.$delete(this.activePrivateSessions, k)
                }
              })
              this.saveChannelState()
              this.syncAllParticipantsTeamState()

              const myUid = (this.room && this.room.localParticipant ? this.room.localParticipant.identity : '') || this.participantIdentity || this.uid || ''
              const myIdent = (this.room && this.room.localParticipant && this.room.localParticipant.identity) || myUid
              const isTarget = msg.studentA === myIdent || msg.studentA === myUid || msg.studentB === myIdent || msg.studentB === myUid

              if (this.room && this.room.localParticipant && isTarget) {
                this.clearChannelState()
                this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
                this.isMicOn = false
                this.isPeerHelpActive = false
                this.updateParticipantState(this.room.localParticipant)
              }
            } else if (msg.type === 'CHANNEL_STATE_SYNC') {
              if (msg.activePrivateSessions && typeof msg.activePrivateSessions === 'object') {
                this.activePrivateSessions = {...msg.activePrivateSessions}
              }
              if (msg.activeTeacherJoinedTeamId !== undefined) {
                this.activeTeacherJoinedTeamId = msg.activeTeacherJoinedTeamId
              }
              this.saveChannelState()
              this.syncAllParticipantsTeamState()

              const myUid = this.participantIdentity
              const myIdent = (this.room && this.room.localParticipant && this.room.localParticipant.identity) || myUid
              const inSession =
                this.activePrivateSessions['ALL'] ||
                this.activePrivateSessions[myUid] ||
                this.activePrivateSessions[myIdent] ||
                Object.keys(this.activePrivateSessions).some((k) => this.activePrivateSessions[k] === myUid || this.activePrivateSessions[k] === myIdent)

              if (this.room && this.room.localParticipant && inSession) {
                if (this.isMicOn) {
                  this.room.localParticipant.setMicrophoneEnabled(true).catch(() => {})
                }
                this.updateParticipantState(this.room.localParticipant)
                if (!this.isTeacher) {
                  this.sendStudentHelpInfoToTeacher(
                    this.activePrivateSessions[myUid] || this.activePrivateSessions[myIdent] || this.activePrivateSessions['ALL']
                  )
                }
              }
            } else if (msg.type === 'TEACHER_ANNOUNCE') {
              const p = this.participants.find((item) => item.sid === msg.sid || item.identity === msg.teacherIdentity || item.identity === msg.identity)
              if (p) {
                this.$set(p, 'isTeacher', true)
              }
            } else if (msg.type === 'request_teacher_announce') {
              if (this.isTeacher && this.room?.localParticipant) {
                const payload = JSON.stringify({
                  type: 'TEACHER_ANNOUNCE',
                  teacherIdentity: this.room.localParticipant.identity,
                  teacherName: this.userName,
                })
                this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})
              }
            } else if (msg.type === 'GAME_ON_START') {
              if (msg.gameOnRecord) {
                this.gameOnRecord = msg.gameOnRecord
              }
              if (this.room && this.room.localParticipant) {
                this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
                this.isMicOn = false
                this.updateParticipantState(this.room.localParticipant)
              }
              this.syncAllParticipantsTeamState()
            } else if (msg.type === 'GAME_ON_STOP') {
              if (msg.gameOnRecord) {
                this.gameOnRecord = msg.gameOnRecord
              }
              this.syncAllParticipantsTeamState()
            } else if (msg.type === 'RAISE_HAND') {
              const p = this.participants.find(
                (item) =>
                  (msg.sid && item.sid === msg.sid) ||
                  (msg.identity && item.identity === msg.identity) ||
                  (msg.uid && (item.uid === msg.uid || item.identity === msg.uid)) ||
                  (participant && (item.sid === participant.sid || item.identity === participant.identity))
              )
              if (p) {
                this.$set(p, 'isHandRaised', msg.isHandRaised)
                if (msg.slideId) this.$set(p, 'slideId', msg.slideId)
                if (msg.questionId) this.$set(p, 'questionId', msg.questionId)
                if (msg.pageIndex != null) this.$set(p, 'pageIndex', msg.pageIndex)
                if (msg.isCpa != null) this.$set(p, 'isCpa', msg.isCpa)
                if (msg.studentDrawKey) this.$set(p, 'studentDrawKey', msg.studentDrawKey)
                if (msg.uid) this.$set(p, 'uid', msg.uid)
                if (msg.sessionId) this.$set(p, 'sessionId', msg.sessionId)
                if (msg.isCpa === false || !msg.cpaDrawUrl) this.$set(p, 'cpaDrawUrl', '')
                else if (msg.cpaDrawUrl) this.$set(p, 'cpaDrawUrl', msg.cpaDrawUrl)
                this.scheduleHandRaiseAutoExpiry(p)

                if (this.isTeacher) {
                  if (!msg.isHandRaised && this.helpModalStudent && (this.helpModalStudent.identity === p.identity || this.helpModalStudent.sid === p.sid)) {
                    this.closeHelpModal()
                  }
                }
              }
            } else if (msg.type === 'TEACHER_ACCEPTED_STUDENT') {
              this.$set(this.activePrivateSessions, msg.studentIdentity, msg.teacherIdentity)
              this.saveChannelState()
              this.syncAllParticipantsTeamState()

              const acceptedStudent = this.participants.find(
                (item) => msg.studentIdentity && (item.identity === msg.studentIdentity || item.uid === msg.studentIdentity)
              )
              if (acceptedStudent) {
                this.$set(acceptedStudent, 'isHandRaised', false)
              }
              if (this.helpModalStudent && (this.helpModalStudent.identity === msg.studentIdentity || this.helpModalStudent.uid === msg.studentIdentity)) {
                this.closeHelpModal()
              }
              if (
                this.channelSwitchModalStudent &&
                (this.channelSwitchModalStudent.identity === msg.studentIdentity || this.channelSwitchModalStudent.uid === msg.studentIdentity)
              ) {
                this.closeChannelSwitchModal()
              }

              if (
                !this.isTeacher &&
                this.room &&
                this.room.localParticipant &&
                (msg.studentIdentity === 'ALL' || msg.studentIdentity === this.room.localParticipant.identity)
              ) {
                this.room.localParticipant.setMicrophoneEnabled(true).catch(() => {})
                this.isMicOn = true
                this.updateParticipantState(this.room.localParticipant)
                this.sendStudentHelpInfoToTeacher(msg.teacherIdentity)
              }
            } else if (msg.type === 'STUDENT_HELP_INFO') {
              if (this.isTeacher) {
                const p = this.participants.find((item) => item.sid === msg.sid || item.identity === msg.studentIdentity)
                if (p && this.isStudentInAnotherTeacherChannel(p)) return

                const myParticipant = this.room?.localParticipant
                const myIds = this.getParticipantIds(myParticipant)
                const isTargetTeacher =
                  (msg.teacherIdentity && myIds.some((id) => id === msg.teacherIdentity || msg.teacherIdentity.includes(id))) ||
                  (!msg.teacherIdentity &&
                    this.activePrivateSessions[msg.studentIdentity] &&
                    myIds.some(
                      (id) => id === this.activePrivateSessions[msg.studentIdentity] || this.activePrivateSessions[msg.studentIdentity].includes(id)
                    )) ||
                  (this.activePrivateSessions['ALL'] &&
                    myIds.some((id) => id === this.activePrivateSessions['ALL'] || this.activePrivateSessions['ALL'].includes(id)))
                if (!isTargetTeacher) return
                const effectiveSlideId = msg.slideId || (p && (p.slideId || p.questionId)) || (this.lastStudentHelpInfo && this.lastStudentHelpInfo.slideId)
                const isCpa = typeof msg.isCpa === 'boolean' ? msg.isCpa : this.isCpaQuestion(effectiveSlideId)
                let newCpaDrawUrl = ''
                if (isCpa) {
                  newCpaDrawUrl = (msg.cpaDrawUrl && msg.cpaDrawUrl !== '/draw/' ? msg.cpaDrawUrl : '') || (p && p.cpaDrawUrl) || ''
                  if (!newCpaDrawUrl && msg.uid && effectiveSlideId && msg.sid) {
                    const drawKey =
                      msg.studentDrawKey || (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.drawkey) || ''
                    if (drawKey) {
                      newCpaDrawUrl = `/draw/#room=${msg.uid}_${effectiveSlideId}_${msg.sid},${drawKey}`
                    }
                  }
                }

                if (p) {
                  this.$set(p, 'cpaDrawUrl', newCpaDrawUrl)
                  if (msg.uid) this.$set(p, 'uid', msg.uid)
                  if (effectiveSlideId) this.$set(p, 'slideId', effectiveSlideId)
                  if (msg.pageIndex != null) this.$set(p, 'pageIndex', msg.pageIndex)
                  if (msg.isCpa != null) this.$set(p, 'isCpa', msg.isCpa)
                  if (msg.studentDrawKey) this.$set(p, 'studentDrawKey', msg.studentDrawKey)
                }

                // Update or add student entry in channelStudents
                let studentEntry = this.channelStudents.find((s) => s.identity === msg.studentIdentity)
                if (studentEntry) {
                  studentEntry.cpaDrawUrl = newCpaDrawUrl
                  if (msg.uid) studentEntry.uid = msg.uid
                  if (effectiveSlideId) studentEntry.slideId = effectiveSlideId
                  if (msg.pageIndex != null) studentEntry.pageIndex = msg.pageIndex
                  studentEntry.isCpa = isCpa
                  if (msg.studentDrawKey) studentEntry.studentDrawKey = msg.studentDrawKey
                  if (p && p.name) studentEntry.name = p.name
                } else if (this.activePrivateSessions[msg.studentIdentity]) {
                  studentEntry = {
                    identity: msg.studentIdentity,
                    name: (p && p.name) || msg.studentIdentity,
                    uid: msg.uid || (p && p.uid) || msg.studentIdentity,
                    slideId: effectiveSlideId,
                    pageIndex: msg.pageIndex != null ? msg.pageIndex : (p && p.pageIndex),
                    isCpa,
                    sid: msg.sid || this.sid,
                    studentDrawKey: msg.studentDrawKey,
                    cpaDrawUrl: newCpaDrawUrl,
                  }
                  this.channelStudents.push(studentEntry)
                }

                // If no active tab or if this student is currently active, activate their whiteboard
                if (!this.activeWhiteboardStudentIdentity || this.activeWhiteboardStudentIdentity === msg.studentIdentity) {
                  this.activeWhiteboardStudentIdentity = msg.studentIdentity
                  this.cpaDrawUrl = isCpa ? newCpaDrawUrl : ''
                }

                this.lastStudentHelpInfo = {
                  identity: msg.studentIdentity,
                  uid: msg.uid || (this.lastStudentHelpInfo && this.lastStudentHelpInfo.uid),
                  slideId: effectiveSlideId,
                  pageIndex: msg.pageIndex != null ? msg.pageIndex : (p && p.pageIndex),
                  isCpa,
                  sid: msg.sid || this.sid,
                  studentDrawKey: msg.studentDrawKey || (this.lastStudentHelpInfo && this.lastStudentHelpInfo.studentDrawKey),
                  cpaDrawUrl: isCpa ? (newCpaDrawUrl || this.cpaDrawUrl) : '',
                }
                this.saveChannelState()
                this.broadcastChannelStudentsUpdated()

                const emitUrl = isCpa ? (this.activeWhiteboardStudentIdentity === msg.studentIdentity ? newCpaDrawUrl || this.cpaDrawUrl : this.cpaDrawUrl) : ''
                const emitHelpData = {
                  ...this.lastStudentHelpInfo,
                  cpaDrawUrl: emitUrl,
                  participant: p,
                  channelStudents: [...this.channelStudents],
                  activeStudentIdentity: this.activeWhiteboardStudentIdentity,
                }
                this.$emit('accepted-student-help', emitHelpData)
                if (window.EventBus) {
                  window.EventBus.$emit('accepted-student-help', emitHelpData)
                }
              }
            } else if (msg.type === 'CHANNEL_TAB_SWITCH') {
              // A teacher should never adopt another teacher's tab switch broadcast
              if (this.isTeacher) return
              const myUid = this.participantIdentity
              const myIdent = (this.room && this.room.localParticipant && this.room.localParticipant.identity) || myUid
              const myTeacher = this.activePrivateSessions[myUid] || this.activePrivateSessions[myIdent] || this.activePrivateSessions['ALL']
              const isFromMyTeacher =
                myTeacher &&
                (!msg.teacherIdentity ||
                  msg.teacherIdentity === myTeacher ||
                  myTeacher.includes(msg.teacherIdentity) ||
                  msg.teacherIdentity.includes(myTeacher))
              if (isFromMyTeacher && msg.activeStudentIdentity) {
                this.activeWhiteboardStudentIdentity = msg.activeStudentIdentity
                if (msg.cpaDrawUrl && msg.cpaDrawUrl !== '/draw/') {
                  this.cpaDrawUrl = msg.cpaDrawUrl
                }
                this.broadcastChannelStudentsUpdated()
              }
            } else if (msg.type === 'TEACHER_LEFT_STUDENT') {
              this.$delete(this.activePrivateSessions, msg.studentIdentity)
              if (msg.studentIdentity === 'ALL' || msg.isGroupDemo) {
                this.$delete(this.activePrivateSessions, 'ALL')
              }
              this.channelStudents = this.channelStudents.filter((s) => s.identity !== msg.studentIdentity)
              const remainingStudents = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
              if (remainingStudents.length === 0) {
                this.activeTeacherJoinedTeamId = null
                this.cpaDrawUrl = ''
                this.activeWhiteboardStudentIdentity = ''
                this.channelStudents = []
              } else if (this.activeWhiteboardStudentIdentity === msg.studentIdentity) {
                const nextSt = this.channelStudents[0]
                if (nextSt) {
                  this.activeWhiteboardStudentIdentity = nextSt.identity
                  if (nextSt.cpaDrawUrl) this.cpaDrawUrl = nextSt.cpaDrawUrl
                }
              }
              this.saveChannelState()
              this.syncAllParticipantsTeamState()
              this.broadcastChannelStudentsUpdated()
              if (
                this.room &&
                this.room.localParticipant &&
                (msg.studentIdentity === 'ALL' || msg.isGroupDemo || msg.studentIdentity === this.room.localParticipant.identity)
              ) {
                this.clearChannelState()
                this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
                this.isMicOn = false
                this.updateParticipantState(this.room.localParticipant)
              }
            }
          } catch (err) {}
        })
        .on(RoomEvent.Disconnected, () => {
          this.isConnected = false
          this.participants = []
          this.$emit('left')
        })
    },

    async connectToMeeting() {
      if (this.isConnecting || this.isConnected) return

      this.isConnecting = true
      this.connectionError = ''

      try {
        const targetRoom = this.roomName || 'MainRoom'

        const response = await App.service('livekit').create({
          roomName: targetRoom,
          participantName: this.userName,
          participantIdentity: this.participantIdentity,
          sid: this.sid,
        })

        const token = typeof response === 'string' ? response : response.token

        // Disconnect existing room if any
        if (this.room) {
          await this.room.disconnect()
          await new Promise((resolve) => setTimeout(resolve, 300))
          this.participants = []
        }

        this.room = new Room({
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: {
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
          },
          publishDefaults: {
            dtx: true,
            videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h90],
          },
          videoCaptureDefaults: {
            resolution: VideoPresets.h180.resolution,
          },
        })

        this.setupRoomListeners(this.room)

        const wsUrl = 'wss://dev-meeting.classcipe.com'
        await this.room.connect(wsUrl, token, {
          autoSubscribe: true,
        })

        // Ensure channel state is restored fresh after connection
        this.restoreChannelState()

        // Update existing remote participants
        this.room.remoteParticipants.forEach((p) => {
          this.updateParticipantState(p)
        })

        if (this.isTeacher && this.room.localParticipant) {
          const payload = JSON.stringify({
            type: 'TEACHER_ANNOUNCE',
            teacherIdentity: this.room.localParticipant.identity,
            teacherName: this.userName,
          })
          this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})
        }

        // Re-sync channel state on connect if restored from session storage
        if (Object.keys(this.activePrivateSessions).length > 0 || this.activeTeacherJoinedTeamId != null) {
          try {
            await this.room.localParticipant.setMicrophoneEnabled(true)
            this.isMicOn = true
          } catch (e) {}

          if (this.isTeacher) {
            const syncPayload = JSON.stringify({
              type: 'CHANNEL_STATE_SYNC',
              activePrivateSessions: {...this.activePrivateSessions},
              activeTeacherJoinedTeamId: this.activeTeacherJoinedTeamId,
            })
            this.room.localParticipant.publishData(new TextEncoder().encode(syncPayload), {reliable: true}).catch(() => {})

            Object.keys(this.activePrivateSessions).forEach((sIdent) => {
              const partner = String(this.activePrivateSessions[sIdent] || '').trim()
              const myIds = this.getParticipantIds(this.room.localParticipant)
              if (sIdent !== 'ALL' && !myIds.some((id) => id === partner || partner.includes(id))) {
                return
              }
              const payloadAccept = JSON.stringify({
                type: 'TEACHER_ACCEPTED_STUDENT',
                studentIdentity: sIdent,
                teacherIdentity: this.room.localParticipant.identity,
              })
              this.room.localParticipant.publishData(new TextEncoder().encode(payloadAccept), {reliable: true}).catch(() => {})
            })

            if (this.lastStudentHelpInfo) {
              this.$emit('accepted-student-help', {
                ...this.lastStudentHelpInfo,
                cpaDrawUrl: this.cpaDrawUrl || this.lastStudentHelpInfo.cpaDrawUrl,
              })
              if (window.EventBus) {
                window.EventBus.$emit('accepted-student-help', {
                  ...this.lastStudentHelpInfo,
                  cpaDrawUrl: this.cpaDrawUrl || this.lastStudentHelpInfo.cpaDrawUrl,
                })
              }
            }
          } else {
            const myUid = this.participantIdentity
            const myIdent = this.room.localParticipant.identity
            const teacherIdent = this.activePrivateSessions['ALL'] || this.activePrivateSessions[myUid] || this.activePrivateSessions[myIdent]
            if (teacherIdent) {
              this.sendStudentHelpInfoToTeacher(teacherIdent)
            }
          }
          this.syncAllParticipantsTeamState()
        }

        // Always broadcast a channel sync request in case peer already has an active channel for us
        const reqSyncPayload = JSON.stringify({
          type: 'REQUEST_CHANNEL_SYNC',
          requesterIdentity: this.room.localParticipant.identity,
          isTeacher: this.isTeacher,
        })
        this.room.localParticipant.publishData(new TextEncoder().encode(reqSyncPayload), {reliable: true}).catch(() => {})

        // Enable local tracks
        try {
          if (!this.audioMuted) {
            await this.room.localParticipant.setMicrophoneEnabled(true)
          }
          if (!this.videoMuted) {
            await this.room.localParticipant.setCameraEnabled(true)
          }
        } catch (mediaErr) {
          console.warn('[EmbeddedMeeting] Could not access media devices:', mediaErr.message)
        }

        this.updateParticipantState(this.room.localParticipant)
        this.room.startAudio().catch(() => {})

        // Register with breakout-rooms service
        try {
          await App.service('breakout-rooms').create({
            sid: this.sid,
            assignments: [{userName: this.userName, roomName: targetRoom, uid: this.uid || ''}],
          })
        } catch (e) {
          console.warn('[EmbeddedMeeting] Failed to register with breakout-rooms:', e.message)
        }

        this.isConnected = true
        this.isConnecting = false
        this.startAudioLevelPolling()
        this.$emit('joined')
      } catch (err) {
        console.error('[EmbeddedMeeting] Connection failed:', err)
        this.connectionError = 'Failed to connect. Please try again.'
        this.isConnecting = false
        this.$emit('error', err)
      }
    },

    startAudioLevelPolling() {
      if (this.audioLevelInterval) return
      this.audioLevelInterval = setInterval(() => {
        if (!this.room) return
        this.participants.forEach((pState, idx) => {
          const liveP = pState.isLocal ? this.room.localParticipant : this.room.remoteParticipants.get(pState.sid)
          if (liveP) {
            const isMicOn = liveP.isMicrophoneEnabled
            const isVideoOn = liveP.isCameraEnabled
            const level = isMicOn ? liveP.audioLevel || 0 : 0
            const speaking = isMicOn && (liveP.isSpeaking || level > 0.04)

            if (pState.isMicOn !== isMicOn) this.$set(pState, 'isMicOn', isMicOn)
            if (pState.isVideoOn !== isVideoOn) this.$set(pState, 'isVideoOn', isVideoOn)
            if (pState.audioLevel !== level) this.$set(pState, 'audioLevel', level)
            if (pState.isSpeaking !== speaking) this.$set(pState, 'isSpeaking', speaking)
          } else {
            if (pState.audioLevel !== 0) this.$set(pState, 'audioLevel', 0)
            if (pState.isSpeaking !== false) this.$set(pState, 'isSpeaking', false)
          }
        })
      }, 50)
    },

    stopAudioLevelPolling() {
      if (this.audioLevelInterval) {
        clearInterval(this.audioLevelInterval)
        this.audioLevelInterval = null
      }
    },

    async disconnectFromMeeting() {
      this.stopAudioLevelPolling()
      if (this.room) {
        try {
          await this.room.disconnect()
        } catch (e) {
          console.warn('[EmbeddedMeeting] Disconnect error:', e)
        }
        this.room = null
      }
      this.participants = []
      this.isConnected = false
      this.isConnecting = false
      this.hasHandRaiseTimedOut = false
      this.$emit('left')
    },

    async toggleMic() {
      if (!this.room?.localParticipant) return
      try {
        const newState = !this.isMicOn
        await this.room.localParticipant.setMicrophoneEnabled(newState)
        this.isMicOn = newState
        this.updateParticipantState(this.room.localParticipant)
      } catch (err) {
        console.warn('[EmbeddedMeeting] Mic toggle failed:', err.message)
      }
    },

    async toggleVideo() {
      if (!this.room?.localParticipant) return
      try {
        const newState = !this.isVideoOn
        await this.room.localParticipant.setCameraEnabled(newState)
        this.isVideoOn = newState
        this.updateParticipantState(this.room.localParticipant)
      } catch (err) {
        console.warn('[EmbeddedMeeting] Camera toggle failed:', err.message)
      }
    },

    toggleRaiseHand(p) {
      if (this.isStudentInTeacherChannel) return
      const target = p || this.participants.find((item) => item.isLocal)
      if (!target) return

      if (this.isTeacherBusyWithAnotherStudent) {
        this.teacherBusyModalVisible = true
        return
      }

      if (this.room?.localParticipant) {
        if (!target.identity) target.identity = this.room.localParticipant.identity || this.participantIdentity || this.uid || ''
        if (!target.sid) target.sid = this.room.localParticipant.sid || ''
      }

      const newState = !target.isHandRaised
      this.$set(target, 'isHandRaised', newState)
      if (target.isLocal) {
        this.hasHandRaiseTimedOut = false
      }
      this.scheduleHandRaiseAutoExpiry(target)

      const targetUid = target.uid || target.identity || this.uid || (this.$store && this.$store.state.user?._id) || ''
      const sid = this.sid || (this.$store && this.$store.state.session?.sid) || ''
      const slideId =
        (this.$store &&
          (this.$store.state.currentPage?._id ||
            this.$store.state.question?._id ||
            this.$store.state.currentSlide?._id ||
            (this.$store.state.sessionPages && this.$store.state.sessionPages[this.$store.state.currentPageIndex]?._id))) ||
        ''
      const studentDrawKey = (this.$store && this.$store.state.session?.drawkey) || ''

      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'RAISE_HAND',
          identity: target.identity,
          name: target.name,
          sid: target.sid,
          sessionId: sid,
          isHandRaised: newState,
          uid: targetUid,
          slideId: slideId,
          studentDrawKey: studentDrawKey,
        })
        const encoded = new TextEncoder().encode(payload)
        this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})
      }
    },

    scheduleHandRaiseAutoExpiry(p) {
      if (!p) return
      const key = p.sid || p.identity || (p.isLocal ? 'local_user' : null)
      if (!key) return

      if (this.handRaiseTimers[key]) {
        clearTimeout(this.handRaiseTimers[key])
        delete this.handRaiseTimers[key]
      }

      if (p.isHandRaised) {
        this.handRaiseTimers[key] = setTimeout(() => {
          this.$set(p, 'isHandRaised', false)
          delete this.handRaiseTimers[key]

          if (p.isLocal) {
            this.hasHandRaiseTimedOut = true
          }

          if (this.room && this.room.localParticipant) {
            const payload = JSON.stringify({
              type: 'RAISE_HAND',
              identity: p.identity,
              name: p.name,
              sid: p.sid,
              isHandRaised: false,
            })
            const encoded = new TextEncoder().encode(payload)
            this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})
          }
        }, 60000)
      }
    },

    async handleTeacherAcceptHelp(p) {
      this.$set(p, 'isHandRaised', false)
      this.scheduleHandRaiseAutoExpiry(p)
      const pUid = p.uid || p.identity || ''
      const pName = p.name || p.identity || ''
      this.activeTeacherJoinedTeamId = null

      this.cpaDrawUrl = p.cpaDrawUrl || ''

      // Fetch necessary student data from main classroom or in game on
      let uid = pUid
      const members =
        (this.$store && this.$store.state && this.$store.state.teacher && this.$store.state.teacher.rooms && this.$store.state.teacher.rooms.members) ||
        (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.members) ||
        []
      const foundMember = members.find((m) => {
        const mId = String(m._id || m.id || m.uid || '')
        const mName = String(Array.isArray(m.name) ? m.name.join(' ') : m.name || m.nickname || '')
          .trim()
          .toLowerCase()
        return (mId && mId === pUid) || (pName && mName && (mName === pName || mName.includes(pName)))
      })
      if (foundMember) {
        uid = foundMember._id || foundMember.id || foundMember.uid || uid
      }

      const sid =
        p.sessionId || this.sid || (this.$store && this.$store.state.session?.sid) || (window.App && window.App.get && window.App.get('session')?.sid) || ''
      console.log('djhsjhdlh', sid)
      let slideId = p.slideId || p.questionId || ''
      if (!slideId && this.$store && this.$store.state) {
        slideId =
          this.$store.state.selectedAnalysisSlide?._id ||
          this.$store.state.currentPage?._id ||
          this.$store.state.question?._id ||
          this.$store.state.currentSlide?._id ||
          (this.$store.state.sessionPages && this.$store.state.sessionPages[this.$store.state.currentPageIndex]?._id) ||
          ''
      }

      let studentDrawKey = p.studentDrawKey || (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.drawkey) || ''

      const targetObj = p.question || p.slide || slideId
      const isCpa = (p && typeof p.isCpa === 'boolean') ? p.isCpa : this.isCpaQuestion(targetObj)

      this.cpaDrawUrl = ''
      if (isCpa) {
        this.cpaDrawUrl = p.cpaDrawUrl || ''
        if (!this.cpaDrawUrl) {
          if (uid && slideId && sid && studentDrawKey) {
            this.cpaDrawUrl = `/draw/#room=${uid}_${slideId}_${sid},${studentDrawKey}`
          } else if (sid && studentDrawKey) {
            this.cpaDrawUrl = `/draw/#room=${sid},${studentDrawKey}`
          }
        }
      } else {
        this.cpaDrawUrl = ''
        if (p) this.$set(p, 'cpaDrawUrl', '')
      }

      this.channelStudents = [
        {
          identity: p.identity,
          name: pName,
          uid,
          slideId,
          pageIndex: p.pageIndex != null ? p.pageIndex : null,
          isCpa,
          sid,
          studentDrawKey,
          cpaDrawUrl: this.cpaDrawUrl,
        },
      ]
      this.activeWhiteboardStudentIdentity = p.identity

      const emitAcceptedData = {
        identity: p.identity,
        uid,
        slideId,
        pageIndex: p.pageIndex != null ? p.pageIndex : null,
        isCpa,
        sid,
        studentDrawKey,
        cpaDrawUrl: this.cpaDrawUrl,
        participant: p,
        channelStudents: [...this.channelStudents],
        activeStudentIdentity: p.identity,
      }

      this.$emit('accepted-student-help', emitAcceptedData)

      if (window.EventBus) {
        window.EventBus.$emit('accepted-student-help', emitAcceptedData)
      }

      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'RAISE_HAND',
          identity: p.identity,
          name: p.name,
          sid: p.sid,
          isHandRaised: false,
        })
        const encoded = new TextEncoder().encode(payload)
        this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})

        const payloadAccept = JSON.stringify({
          type: 'TEACHER_ACCEPTED_STUDENT',
          studentIdentity: p.identity,
          teacherIdentity: this.room.localParticipant.identity,
        })
        const encodedAccept = new TextEncoder().encode(payloadAccept)
        this.room.localParticipant.publishData(encodedAccept, {reliable: true}).catch(() => {})

        this.$set(this.activePrivateSessions, p.identity, this.room.localParticipant.identity)
        this.lastStudentHelpInfo = {
          identity: p.identity,
          uid,
          slideId,
          sid,
          studentDrawKey,
          cpaDrawUrl: this.cpaDrawUrl,
        }
        this.saveChannelState()
        this.syncAllParticipantsTeamState()
        this.broadcastChannelStudentsUpdated()

        this.room.localParticipant.setMicrophoneEnabled(true).catch(() => {})
        this.isMicOn = true
        this.updateParticipantState(this.room.localParticipant)
        this.updateParticipantState(p)
      }
    },

    handleTeacherLeaveHelp(p) {
      const studentId = p ? p.identity : ''
      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'TEACHER_LEFT_STUDENT',
          studentIdentity: studentId,
          teacherIdentity: this.room.localParticipant.identity,
        })
        const encoded = new TextEncoder().encode(payload)
        this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})

        if (studentId) {
          this.$delete(this.activePrivateSessions, studentId)
        }
        this.channelStudents = this.channelStudents.filter((s) => s.identity !== studentId)

        const remainingStudents = Object.keys(this.activePrivateSessions).filter((k) => k !== 'ALL')
        if (remainingStudents.length === 0) {
          this.cpaDrawUrl = ''
          this.activeWhiteboardStudentIdentity = ''
          this.channelStudents = []
          this.activeTeacherJoinedTeamId = null
          this.clearChannelState()
          this.syncAllParticipantsTeamState()
          this.broadcastChannelStudentsUpdated()

          this.$emit('left-student-help', {
            identity: studentId,
            participant: p,
          })
          if (window.EventBus) {
            window.EventBus.$emit('left-student-help', {
              identity: studentId,
              participant: p,
            })
            window.EventBus.$emit('teacher-left-help', p)
          }

          this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
          this.isMicOn = false
          this.updateParticipantState(this.room.localParticipant)
          if (p) this.updateParticipantState(p)
        } else {
          if (this.activeWhiteboardStudentIdentity === studentId) {
            const nextStudent = this.channelStudents[0]
            if (nextStudent) {
              this.handleTeacherSwitchChannelTab(nextStudent)
            }
          }
          this.saveChannelState()
          this.syncAllParticipantsTeamState()
          this.broadcastChannelStudentsUpdated()
          this.broadcastChannelStateSync()
          if (p) this.updateParticipantState(p)
        }
      }
    },

    handleStudentLeavePeerHelp(p) {
      if (this.room && this.room.localParticipant) {
        const myIdent = this.room.localParticipant.identity
        const partnerIdent = p?.identity || p?.uid || ''
        const payload = JSON.stringify({
          type: 'PEER_HELP_LEFT',
          studentA: myIdent,
          studentB: partnerIdent,
        })
        const encoded = new TextEncoder().encode(payload)
        this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})

        Object.keys(this.activePrivateSessions).forEach((k) => {
          if (k === myIdent || k === partnerIdent || this.activePrivateSessions[k] === myIdent || this.activePrivateSessions[k] === partnerIdent) {
            this.$delete(this.activePrivateSessions, k)
          }
        })
        this.isPeerHelpActive = false
        this.clearChannelState()
        this.syncAllParticipantsTeamState()

        const syncPayload = JSON.stringify({
          type: 'CHANNEL_STATE_SYNC',
          activePrivateSessions: {...this.activePrivateSessions},
          activeTeacherJoinedTeamId: this.activeTeacherJoinedTeamId,
        })
        this.room.localParticipant.publishData(new TextEncoder().encode(syncPayload), {reliable: true}).catch(() => {})

        this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
        this.isMicOn = false
        this.updateParticipantState(this.room.localParticipant)
      }
    },

    handleTeacherCloseWhiteboard() {
      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'TEACHER_LEFT_STUDENT',
          studentIdentity: '',
          teacherIdentity: this.room.localParticipant.identity,
        })
        this.room.localParticipant.publishData(new TextEncoder().encode(payload), {reliable: true}).catch(() => {})
      }
      this.activePrivateSessions = {}
      this.channelStudents = []
      this.activeWhiteboardStudentIdentity = ''
      this.cpaDrawUrl = ''
      this.activeTeacherJoinedTeamId = null
      this.clearChannelState()
      this.syncAllParticipantsTeamState()
      this.broadcastChannelStudentsUpdated()
      if (this.room && this.room.localParticipant) {
        this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
        this.isMicOn = false
        this.updateParticipantState(this.room.localParticipant)
      }
      if (window.EventBus) {
        window.EventBus.$emit('left-student-help', {identity: ''})
        window.EventBus.$emit('teacher-left-help')
      }
    },

    handleTeacherDismissHelp(p) {
      this.$set(p, 'isHandRaised', false)
      this.scheduleHandRaiseAutoExpiry(p)
      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'RAISE_HAND',
          identity: p.identity,
          name: p.name,
          sid: p.sid,
          isHandRaised: false,
        })
        const encoded = new TextEncoder().encode(payload)
        this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})
      }
    },
    handleStartLiveDemo(data) {
      if (!data) return
      this.isLiveDemoSession = true
      const isGroupDemo = !!data.isGroupDemo
      const studentIdentity = data.studentId

      if (!isGroupDemo && studentIdentity) {
        const pTeamId = this.getTeamIdForUser(studentIdentity)

        if (pTeamId != null) {
          this.activeTeacherJoinedTeamId = pTeamId
          this.syncAllParticipantsTeamState()
          this.$emit('joined-team-channel', pTeamId)
        }
      }

      if (this.room && this.room.localParticipant) {
        const payloadAccept = JSON.stringify({
          type: 'TEACHER_ACCEPTED_STUDENT',
          studentIdentity: isGroupDemo ? 'ALL' : studentIdentity,
          teacherIdentity: this.room.localParticipant.identity,
          isLiveDemo: true,
          isGroupDemo: isGroupDemo,
          questionId: data.questionId,
          cpaDrawUrl: data.cpaDrawUrl,
        })
        const encodedAccept = new TextEncoder().encode(payloadAccept)
        this.room.localParticipant.publishData(encodedAccept, {reliable: true}).catch(() => {})

        if (isGroupDemo) {
          this.$set(this.activePrivateSessions, 'ALL', this.room.localParticipant.identity)
        } else if (studentIdentity) {
          this.$set(this.activePrivateSessions, studentIdentity, this.room.localParticipant.identity)
        }
        this.saveChannelState()
        this.syncAllParticipantsTeamState()

        this.room.localParticipant.setMicrophoneEnabled(true).catch(() => {})
        this.isMicOn = true
        this.updateParticipantState(this.room.localParticipant)
        if (studentIdentity) {
          const p = this.participants.find((item) => item.identity === studentIdentity || item.sid === studentIdentity)
          if (p) this.updateParticipantState(p)
        }
      }
    },

    handleEndLiveDemo(data) {
      this.isLiveDemoSession = false
      const isGroupDemo = data && data.isGroupDemo
      const studentIdentity = isGroupDemo ? 'ALL' : (data && data.studentId) || Object.keys(this.activePrivateSessions)[0]
      if (this.room && this.room.localParticipant) {
        const payload = JSON.stringify({
          type: 'TEACHER_LEFT_STUDENT',
          studentIdentity: studentIdentity || 'ALL',
          teacherIdentity: this.room.localParticipant.identity,
          isGroupDemo: !!isGroupDemo,
        })
        const encoded = new TextEncoder().encode(payload)
        this.room.localParticipant.publishData(encoded, {reliable: true}).catch(() => {})

        if (studentIdentity) {
          this.$delete(this.activePrivateSessions, studentIdentity)
        }
        this.$delete(this.activePrivateSessions, 'ALL')
        this.activeTeacherJoinedTeamId = null
        this.clearChannelState()
        this.syncAllParticipantsTeamState()

        this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
        this.isMicOn = false
        this.updateParticipantState(this.room.localParticipant)
      }
    },

    async handlePeerHelpClick() {
      this.$emit('peer-help-click')
      if (window.EventBus) {
        window.EventBus.$emit('peer-help-click')
      }

      if (!App || !App.service('game-on')) return

      const tId = this.getTeamIdForUser(this.uid) || null
      const gameConfigId = this.gameOnRecord?._id || null
      if (!tId || !gameConfigId) return

      this.isPeerHelpLoading = true
      try {
        await App.service('game-on').get('sendHelpRequest', {
          query: {
            _id: gameConfigId,
            teamId: tId,
          },
        })
      } catch (e) {
        console.error('Peer help request error:', e)
      } finally {
        this.isPeerHelpLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.embedded-meeting-sidebar {
  width: 280px;
  height: 100%;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.embedded-meeting-sidebar.is-collapsed {
  width: 140px;
}

/* Header Styles */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #0f172a;
  color: white;
  height: 48px;
  padding: 0 16px;
  flex-shrink: 0;
}

.sidebar-header .header-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar-header .header-text {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.sidebar-header .header-icon {
  font-size: 18px;
}

.header-toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.header-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.header-toggle-btn i {
  font-size: 14px;
}

.sidebar-tiles-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 2px;
  background: #f8fafc;
}

/* Status / Loading */
.sidebar-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  height: 100%;
}

.sidebar-status-text {
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
}

.sidebar-error {
  gap: 8px;
}

.sidebar-retry-btn {
  margin-top: 8px;
  padding: 6px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2563eb;
  }
}

.sidebar-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: sidebar-spin 0.8s linear infinite;
}

@keyframes sidebar-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Tiles Container */
.sidebar-tiles-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-tiles {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 8px;
  padding-bottom: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
}

.sidebar-tile-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 10px; /* Replaces gap */
}

.sidebar-tiles > .sidebar-tile-wrapper.is-top-teacher-wrapper {
  order: 1;
}

.sidebar-tiles > .private-channel-group-container.is-my-channel-container {
  order: 1;
}

.sidebar-tiles > .private-channel-group-container {
  order: 2;
}

.sidebar-tiles > .sidebar-tile-wrapper {
  order: 3;
}

/* Unified container for private channel participants (Teacher + all connected students in one container) */
.private-channel-group-container {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
  border-radius: 14px;
  padding: 8px 0 12px 0;
  width: 95%;
  margin: 0 auto 16px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.in-channel-tile-wrapper {
  margin-bottom: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  width: 100% !important;
}

/* Individual Tile */
.sidebar-tile {
  position: relative;
  width: 88%;
  height: 105px;
  border-radius: 10px;
  overflow: hidden;
  background: #1e1e2a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &.is-teacher-tile {
    width: 94%;
    height: 125px;
    border: 1.5px solid rgba(59, 130, 246, 0.35);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

    &.is-expanded {
      width: 96%;
      height: 145px;
    }

    .sidebar-avatar-circle {
      width: 58px;
      height: 58px;
      font-size: 20px;
    }

    .sidebar-tile-name {
      max-width: 95px;
    }
  }

  &.is-clickable-for-teacher {
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
      transform: translateY(-1px);
    }
  }

  &.is-expanded {
    width: 92%;
    height: 128px;
    box-shadow: 0 0 0 2.5px #22c55e, 0 4px 16px rgba(34, 197, 94, 0.35);
  }

  &.is-speaking:not(.is-expanded) {
    box-shadow: 0 0 0 2px #22c55e;
  }
}

.sidebar-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}

.sidebar-avatar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 8px;
  box-sizing: border-box;
  user-select: none;
  background-color: #434655;
}

.sidebar-avatar-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  user-select: none;
  flex-shrink: 0;

  &.is-teacher-circle {
    background-color: #3c82f6;
  }

  &.is-student-circle {
    background-color: #079669;
  }
}

.sidebar-tile-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 5px 8px;
  background: rgba(30, 41, 59, 0.85); /* Dark solid/translucent background */
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.embedded-meeting-sidebar.is-collapsed {
  .sidebar-tiles {
    padding: 10px 0;
    gap: 10px;
  }

  .private-channel-group-container {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin-bottom: 8px !important;
    gap: 10px !important;
    width: 100% !important;
  }

  .sidebar-tile-wrapper {
    margin-bottom: 10px;
    width: 100%;
  }

  .sidebar-tile {
    width: 88% !important;
    height: 94px !important;
    min-height: 94px !important;
    border-radius: 12px !important;
    background: #f4f6fa !important;
    border: 1.5px solid transparent !important;
    box-shadow: none !important;
    overflow: hidden !important;
    margin: 0 auto !important;

    &.has-no-badge {
      height: 80px !important;
      min-height: 80px !important;
    }

    &.is-teacher-tile,
    &.is-local-tile {
      background: #ffffff !important;
      border: 1.5px solid #20d999 !important;
    }

    &.is-speaking {
      border-color: #20d999 !important;
    }
  }

  .in-channel-tile-wrapper .sidebar-tile {
    background: #ffffff !important;
    border: 1.5px solid #20d999 !important;
  }

  .sidebar-tile-label {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}

.sidebar-collapsed-card {
  width: 100%;
  height: 100%;
  padding: 10px 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  cursor: pointer;
}

.collapsed-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.collapsed-badge.teacher-badge {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #1e293b;
  width: fit-content;

  .badge-emoji {
    font-size: 13px;
    line-height: 1;
  }
}

.collapsed-badge.local-badge {
  background: #20d999;
  border-radius: 4px;
  padding: 2px 8px;
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  width: fit-content;
}

.collapsed-icons {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
}

.sidebar-tile-name-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sidebar-tile-name {
  font-size: 11px;
  color: white;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px; /* Prevent long names from pushing icons off */
}

.sidebar-local-badge {
  background: #10b981;
  color: #064e3b;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.sidebar-teacher-badge {
  background: #2563eb;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.sidebar-tile-icons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sidebar-tile-icons i {
  color: #22c55e;
  transition: all 0.2s ease;
}

.sidebar-tile-icons i.is-muted {
  color: #ef4444;
}

.sidebar-tile-icons i.clickable-icon {
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
}

.sidebar-tile-icons i.clickable-icon:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.15);
}

/* Self Controls */
.sidebar-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 8px;
  background: rgba(0, 0, 0, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.sidebar-ctrl-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &.is-off {
    background: rgba(239, 68, 68, 0.25);
    color: #ef4444;

    &:hover {
      background: rgba(239, 68, 68, 0.4);
    }
  }
}

.team-channel-blurred {
  filter: blur(2.5px) grayscale(0.2) !important;
  transform: scale(1.05);
  transition: filter 0.3s ease;
  pointer-events: none;
}

@keyframes raise-hand-blink {
  0% {
    box-shadow: 0 0 0 2px #0ea5e9;
    border-color: #0ea5e9;
  }
  50% {
    box-shadow: 0 0 12px 3px rgba(14, 165, 233, 0.5);
    border-color: #38bdf8;
  }
  100% {
    box-shadow: 0 0 0 2px #0ea5e9;
    border-color: #0ea5e9;
  }
}

.sidebar-tile.is-blinking {
  animation: raise-hand-blink 2s infinite ease-in-out;
  border: 3px solid #0ea5e9 !important;
  z-index: 10;
}

.raise-hand-bubble {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ffffff;
  border-radius: 20px;
  padding: 4px 6px 4px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  z-index: 20;
  cursor: pointer;
  transition: transform 0.1s ease;
}

.raise-hand-bubble:hover {
  transform: scale(1.03);
}

.raise-hand-emoji {
  font-size: 14px;
}

.raise-hand-bubble-text {
  font-size: 11px;
  font-weight: 500;
  color: #1e293b;
}

.raise-hand-accept-btn {
  background: #22c55e;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  transition: background 0.2s;
}

.raise-hand-accept-btn:hover {
  background: #16a34a;
}

.raise-hand-waiting {
  font-size: 9px;
  font-weight: 600;
  color: #fbbf24;
  display: flex;
  align-items: center;
}

/* Remove Button on Student Card */
.student-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #c62828;
  color: #ffffff;
  border: none;
  border-radius: 20px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  z-index: 25;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    background: #b71c1c;
    transform: scale(1.04);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.45);
  }

  &:active {
    transform: scale(0.96);
  }
}

.student-remove-icon {
  display: block;
  flex-shrink: 0;
}

.student-remove-text {
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.2px;
}

.blurred-overlay-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  z-index: 5;
  pointer-events: none;
}

/* Bottom Controls */
.sidebar-bottom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 12px 14px;
  background: white;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 20;
  box-sizing: border-box;
}

.footer-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.footer-action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  padding: 7px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  font-family: inherit;
  box-sizing: border-box;

  &:hover {
    background: #f8fafc;
    border-color: #64748b;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled,
  &.is-disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
    background: #f1f5f9;
    border-color: #cbd5e1;
  }

  &.lower-hand-btn {
    background: #0f172a;
    border-color: #0f172a;
    color: #ffffff;

    &:hover {
      background: #020617;
    }
  }
}

.footer-btn-emoji {
  font-size: 15px;
  line-height: 1;
}

.footer-btn-text {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}

.footer-media-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}

.sidebar-ctrl-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #181e2e;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  &:hover {
    background: #253046;
  }

  &.is-muted-btn {
    background: #dc2626;

    &:hover {
      background: #b91c1c;
    }
  }
}

.embedded-meeting-sidebar.is-collapsed {
  .sidebar-bottom-controls {
    padding: 12px 10px;
    gap: 10px;
  }

  .footer-actions-row {
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  .footer-action-btn {
    flex: 1;
    max-width: 54px;
    height: 52px;
    border-radius: 12px;
    border: 1.5px solid #94a3b8;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    .footer-btn-text {
      display: none;
    }

    .footer-btn-emoji {
      font-size: 24px;
      line-height: 1;
    }
  }

  .footer-media-row {
    justify-content: center;
    gap: 10px;
  }

  .sidebar-ctrl-btn {
    width: 36px;
    height: 36px;
  }
}

/* Help Student Confirmation Modal */
.help-student-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647;
  padding: 16px;
  box-sizing: border-box;
}

.help-student-modal-card {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 35px -5px rgba(0, 0, 0, 0.15), 0 10px 15px -5px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  animation: helpModalPop 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes helpModalPop {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.help-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.help-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.help-modal-close-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.help-modal-close-btn:hover {
  background: #f1f5f9;
  color: #475569;
}

.help-modal-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.help-modal-student-headline {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.help-modal-student-name {
  color: #0f172a;
  font-weight: 700;
}

.help-modal-desc {
  margin: 0;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
}

.help-modal-info-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 6px;
  margin-bottom: 8px;
}

.help-modal-info-icon {
  font-size: 18px;
  color: #3b82f6;
  flex-shrink: 0;
  margin-top: 1px;
}

.help-modal-info-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}

.help-modal-footer {
  margin-top: 20px;
}

.help-modal-action-btn {
  width: 100%;
  height: 48px;
  background: #0f172a;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.help-modal-action-btn:hover {
  background: #1e293b;
}

.help-modal-action-btn:active {
  transform: scale(0.99);
}

.leave-modal-desc {
  font-size: 15px;
  color: #334155;
  line-height: 1.55;
}

.leave-modal-desc strong {
  color: #0f172a;
  font-weight: 700;
}
</style>